(function () {
  'use strict';

  var SESSION_KEY = 'dyefor-ai-session-v1';
  var ACTIVE_JOB_KEY = 'dyefor-ai-active-job-v1';
  var PROGRESS_TIMING_KEY = 'dyefor-ai-progress-timing-v1';
  var DEVICE_STORAGE_KEY = 'dyefor:selected-device';
  var DEVICE_TYPE_STORAGE_KEY = 'dyefor:selected-device-type';
  var MAX_FILE_BYTES = 8 * 1024 * 1024;
  var POLL_INTERVAL_MS = 1500;
  var MAX_POLL_ATTEMPTS = 180;
  // Artwork-ready timings from production, excluding queue admission and mockups.
  var GENERATION_DURATION_DEFAULTS = { phone_case: 32000, banner: 32000, wrapping_paper: 49000 };

  var PRODUCT_LABELS = {
    wrapping_paper: 'Wrapping paper',
    phone_case: 'Phone case',
    banner: 'Banner'
  };

  var GIFT_RELATIONSHIPS = [
    { name: 'granddaughter', aliases: ['granddaughter', 'granddaughters', 'grand daughter', 'grand daughters', 'grandaughter', 'grandaughters'] },
    { name: 'grandson', aliases: ['grandson', 'grandsons', 'grand son', 'grand sons'] },
    { name: 'daughter', aliases: ['daughter', 'daughters'] },
    { name: 'son', aliases: ['son', 'sons'] },
    { name: 'niece', aliases: ['niece', 'nieces'] },
    { name: 'nephew', aliases: ['nephew', 'nephews'] },
    { name: 'sister', aliases: ['sister', 'sisters'] },
    { name: 'brother', aliases: ['brother', 'brothers'] },
    { name: 'wife', aliases: ['wife'] },
    { name: 'husband', aliases: ['husband'] },
    { name: 'partner', aliases: ['partner'] },
    { name: 'mum', aliases: ['mum', 'mom', 'mother'] },
    { name: 'dad', aliases: ['dad', 'father'] },
    { name: 'grandmother', aliases: ['grandmother', 'grandma', 'nan', 'nana'] },
    { name: 'grandfather', aliases: ['grandfather', 'grandad', 'granddad', 'grandpa'] },
    { name: 'friend', aliases: ['friend'] }
  ];

  var POSSESSIVE_GIFT_RELATIONSHIPS = [
    { name: 'daughter', aliases: ['girl', 'girls'] },
    { name: 'son', aliases: ['boy', 'boys'] },
    { name: 'husband', aliases: ['man', 'mans'] }
  ];

  function normaliseRelationshipText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[\u2018\u2019']/g, '')
      .replace(/[^a-z\s-]/g, ' ')
      .replace(/\b(?:my|our|a|the|dear|young|younger|youngest|older|oldest|eldest)\b/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function canonicalGiftRelationship(value) {
    var possessiveRelationship = possessiveGiftRelationshipFromText(value);
    if (possessiveRelationship) return possessiveRelationship;
    var normalised = normaliseRelationshipText(value);
    if (!normalised) return '';
    var relationship = GIFT_RELATIONSHIPS.find(function (entry) {
      return entry.aliases.indexOf(normalised) !== -1;
    });
    return relationship ? relationship.name : '';
  }

  function giftRelationshipFromText(value) {
    var normalised = String(value || '')
      .toLowerCase()
      .replace(/[\u2018\u2019']/g, '')
      .replace(/[^a-z\s-]/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!normalised) return '';
    var relationship = GIFT_RELATIONSHIPS.concat(POSSESSIVE_GIFT_RELATIONSHIPS).find(function (entry) {
      return entry.aliases.some(function (alias) {
        var escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
        return new RegExp('\\b(?:my|our)\\s+(?:dear\\s+|little\\s+|young(?:er|est)?\\s+|old(?:er|est)?\\s+|eldest\\s+)?' + escaped + '\\b', 'i').test(normalised);
      });
    });
    return relationship ? relationship.name : '';
  }

  function possessiveGiftRelationshipFromText(value) {
    return giftRelationshipFromText(value);
  }

  function ambiguousGiftAudienceValue(value) {
    var normalised = normaliseRelationshipText(value);
    return /^(?:boy|boys|girl|girls|man|mans|men|woman|women)$/.test(normalised) ? normalised : '';
  }

  function genericGiftAudienceFromText(value) {
    if (giftRelationshipFromText(value)) return '';
    var text = String(value || '')
      .toLowerCase()
      .replace(/[\u2018\u2019']/g, '')
      .replace(/[^a-z\s-]/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    var match = text.match(/\b(?:for\s+)?(?:a|an|the)\s+(?:little\s+|young(?:er|est)?\s+)?(boy|girl|man|woman)\b/)
      || text.match(/\bfor\s+(?:little\s+|young(?:er|est)?\s+)?(boys|girls|men|women)\b/);
    if (!match) return '';
    var labels = { boy: 'a boy', boys: 'boys', girl: 'a girl', girls: 'girls', man: 'a man', men: 'men', woman: 'a woman', women: 'women' };
    return labels[match[1]] || '';
  }

  function isGenericGiftRecipient(value) {
    var text = String(value || '')
      .toLowerCase()
      .replace(/[\u2018\u2019']/g, '')
      .replace(/[^a-z\s-]/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) return false;
    return /^(?:just\s+)?(?:generic|general|anyone|anybody|everyone|no ?one|nobody)(?:\s+(?:design|one|person|recipient|in particular))?$/.test(text)
      || /\b(?:no ?one|nobody|not (?:for )?(?:anyone|anybody))\s+(?:in\s+)?particular\b/.test(text)
      || /\bnot\s+for\s+(?:a|any)\s+specific\s+(?:person|recipient)\b/.test(text)
      || /\bfor\s+(?:anyone|anybody|everyone)\b/.test(text);
  }

  var PHONE_CASE_AESTHETICS = [
    {
      label: 'Floral',
      direction: 'Contemporary floral and botanical artwork with polished colour balance, using either scattered blooms or a considered focal arrangement to suit the selected layout'
    },
    {
      label: 'Animals',
      direction: 'Stylish animal-led artwork or animal print with personality, fashion-led and polished rather than generic clip art or childish unless specifically requested'
    },
    {
      label: 'Food',
      direction: 'Playful food and drink motifs treated as a contemporary accessory graphic, with editorial colour, crisp shapes and an intentional repeat or composition'
    },
    {
      label: 'Bowcore',
      direction: 'Fashionable bowcore styling with ribbons, bows and delicate fabric-inspired details, feminine and current with a clean professional finish'
    },
    {
      label: 'Typography',
      direction: 'Typography-led accessory artwork with strong lettering hierarchy, excellent legibility, intentional spacing and supporting graphic details'
    },
    {
      label: 'Chrome',
      direction: 'Liquid-metal chrome and reflective accents translated into crisp print artwork, with controlled highlights, dimensional forms and useful negative space'
    },
    {
      label: 'Abstract',
      direction: 'Contemporary abstract artwork using expressive shapes, lines and colour relationships, composed deliberately for a phone case rather than as a generic background'
    },
    {
      label: 'Stripes',
      direction: 'Continuous stripes with confident rhythm and coordinated colours. Preserve connected bands; follow the selected composition for an opaque background or deliberately transparent gaps',
      defaultCompositionMode: 'full_bleed'
    },
    {
      label: 'Summer',
      direction: 'Sun-washed summer styling with playful seasonal motifs, fresh colour and a polished holiday-accessory feel'
    },
    {
      label: 'Spring',
      direction: 'Fresh spring styling with light colour, new-season botanical or nature details and a bright contemporary finish'
    },
    {
      label: 'Art',
      direction: 'Original gallery and art-print-inspired artwork with a strong visual point of view, balanced specifically for the phone case format'
    },
    {
      label: 'Minimal',
      direction: 'Refined minimal artwork with a restrained palette, clean forms, generous negative space and precise placement'
    },
    {
      label: 'Cute',
      direction: 'Playful and charming artwork with friendly motifs and joyful colour, polished and contemporary rather than infantile'
    },
    {
      label: 'Pinkcore',
      direction: 'Pink-forward styling with layered pink tones, confident feminine motifs and a current fashion-accessory finish'
    },
    {
      label: 'Cottagecore',
      direction: 'Romantic cottagecore artwork with wildflowers, pastoral details and gentle vintage character, kept clean enough for a modern accessory'
    },
    {
      label: 'Japanese',
      direction: 'Original Japanese-inspired graphic restraint, pattern and colour with elegant composition, without copying brands, characters or known artworks'
    },
    {
      label: 'Y2K',
      direction: 'A current Y2K revival with playful early-2000s forms, glossy accents, bold colour and polished fashion styling'
    },
    {
      label: 'Baddie',
      direction: 'Bold, confident baddie styling with high-contrast fashion graphics, assertive colour and a sleek social-first finish'
    },
    {
      label: 'Fairycore',
      direction: 'Ethereal fairycore artwork with delicate botanicals, tiny stars or wing-like details and a luminous dreamlike palette'
    },
    {
      label: 'Coquette',
      direction: 'Polished coquette styling with ribbons, lace, hearts and soft vintage romance, balanced with contemporary spacing and restraint'
    },
    {
      label: 'Dark Academia',
      direction: 'Dark academia styling with deep scholarly colour, vintage print motifs and literary or architectural detail in a refined composition'
    },
    {
      label: 'Old Money',
      direction: 'Understated old-money styling with heritage-inspired details, a classic restrained palette and quiet luxury rather than obvious branding'
    },
    {
      label: 'Kawaii',
      direction: 'Original kawaii visual styling with charming motifs, expressive details and cheerful colour in a polished, well-balanced composition'
    }
  ].sort(function (first, second) {
    return first.label.localeCompare(second.label, 'en', { sensitivity: 'base' });
  });

  function readStorage(key) {
    try {
      var value = sessionStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {}
  }

  function removeStorage(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {}
  }

  function delay(milliseconds) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, milliseconds);
    });
  }

  function fileToDataUrl(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () { resolve(reader.result); };
      reader.onerror = function () { reject(new Error('That photo could not be read.')); };
      reader.readAsDataURL(file);
    });
  }

  function apiError(message, code, status) {
    var error = new Error(message || 'Something went wrong. Please try again.');
    error.code = code || 'request_failed';
    error.status = status || 0;
    return error;
  }

  function makeElement(tagName, className, text) {
    var element = document.createElement(tagName);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  }

  function loadCanvasImage(source) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      image.decoding = 'async';
      if (!/^data:/i.test(source)) image.crossOrigin = 'anonymous';
      image.onload = function () { resolve(image); };
      image.onerror = function () { reject(new Error('Preview image could not be loaded.')); };
      image.src = source;
      if (image.complete && image.naturalWidth) resolve(image);
    });
  }

  async function loadSavedPreview(source, isCurrent) {
    // A saved KV asset can take longer to reach the customer's region than its
    // status record. Retry only image delivery, never the generation request.
    for (var attempt = 0; attempt < 30; attempt += 1) {
      if (!isCurrent()) return null;
      if (attempt) await delay(5000);
      if (!isCurrent()) return null;
      try {
        return await new Promise(function (resolve, reject) {
          var image = new Image();
          image.crossOrigin = 'anonymous';
          var timer = window.setTimeout(function () { finish(new Error('Preview delivery timed out.')); }, 15000);
          function finish(error) {
            window.clearTimeout(timer);
            image.onload = null;
            image.onerror = null;
            if (error) reject(error);
            else resolve(image);
          }
          image.onload = function () {
            finish(image.naturalWidth ? null : new Error('Empty preview.'));
          };
          image.onerror = function () { finish(new Error('Preview not available yet.')); };
          image.src = source + (source.indexOf('?') === -1 ? '?' : '&') + 'preview_load=' + Date.now() + '-' + attempt;
        });
      } catch (error) {}
    }
    throw apiError('Your design is saved, but its preview is still loading. Check your saved design again.', 'preview_delivery_pending', 503);
  }

  function makePreviewCanvas(width, height) {
    var surface = document.createElement('canvas');
    surface.width = width;
    surface.height = height;
    return surface;
  }

  function drawCanvasCover(context, image, x, y, width, height) {
    var sourceRatio = image.naturalWidth / image.naturalHeight;
    var targetRatio = width / height;
    var sourceWidth = image.naturalWidth;
    var sourceHeight = image.naturalHeight;
    var sourceX = 0;
    var sourceY = 0;
    if (sourceRatio > targetRatio) {
      sourceWidth = image.naturalHeight * targetRatio;
      sourceX = (image.naturalWidth - sourceWidth) / 2;
    } else {
      sourceHeight = image.naturalWidth / targetRatio;
      sourceY = (image.naturalHeight - sourceHeight) / 2;
    }
    context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
  }

  function previewPixels(image, width, height) {
    var surface = makePreviewCanvas(width, height);
    var context = surface.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, width, height);
    return context.getImageData(0, 0, width, height).data;
  }

  function compositeProductionMockup(canvas, artworkSurface, clearReference, blackReference, whiteReference) {
    var width = canvas.width;
    var height = canvas.height;
    var context = canvas.getContext('2d', { willReadFrequently: true });
    var artwork = artworkSurface.getContext('2d', { willReadFrequently: true })
      .getImageData(0, 0, width, height).data;
    var clearPixels = previewPixels(clearReference, width, height);
    var blackPixels = previewPixels(blackReference, width, height);
    var whitePixels = previewPixels(whiteReference, width, height);
    var result = context.createImageData(width, height);

    // Black and white small2 renders describe the PSD's exact per-pixel light response.
    for (var index = 0; index < result.data.length; index += 4) {
      var alpha = artwork[index + 3] / 255;
      for (var channel = 0; channel < 3; channel += 1) {
        var printed = blackPixels[index + channel]
          + ((whitePixels[index + channel] - blackPixels[index + channel])
            * (artwork[index + channel] / 255));
        result.data[index + channel] = Math.round(
          clearPixels[index + channel] * (1 - alpha) + printed * alpha
        );
      }
      result.data[index + 3] = 255;
    }
    context.putImageData(result, 0, 0);
  }

  function drawInstantPhonePreview(canvas, artwork, references) {
    var artworkSurface = makePreviewCanvas(800, 800);
    var context = artworkSurface.getContext('2d');
    // iPhone-14plus-back-silver.config at the production 800px output size.
    drawCanvasCover(context, artwork, 223, 53, 344, 707);
    compositeProductionMockup(canvas, artworkSurface, references[0], references[1], references[2]);
  }

  function drawInstantBannerPreview(canvas, artwork, references) {
    var artworkSurface = makePreviewCanvas(800, 800);
    var context = artworkSurface.getContext('2d');
    var fitted = makePreviewCanvas(684, 235);
    var fittedContext = fitted.getContext('2d');
    drawCanvasCover(fittedContext, artwork, 0, 0, 684, 235);

    // 6x2.config: exact control points plus the scaled 5x1600 fabric wave.
    for (var x = 0; x < 684; x += 2) {
      var sliceWidth = Math.min(2, 684 - x);
      var waveOffset = Math.sin(Math.PI * ((x + sliceWidth / 2) / 684)) * 5;
      context.drawImage(fitted, x, 0, sliceWidth, 235, 59 + x, 255 + waveOffset, sliceWidth, 235);
    }
    compositeProductionMockup(canvas, artworkSurface, references[0], references[1], references[2]);
  }

  class DyeforAIHero extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.dataset.studioVersion = '20260914-product-choice-first';
      this.apiBase = String(this.dataset.apiBase || '').replace(/\/$/, '');
      this.openButton = this.querySelector('[data-ai-open]');
      this.openButtonLabel = this.querySelector('[data-ai-open-label]');
      this.productInputs = this.querySelectorAll('[data-ai-product-choice]');
      this.phoneDeviceField = this.querySelector('[data-ai-phone-device-field]');
      this.phoneDeviceSelect = this.querySelector('[data-ai-phone-device]');
      this.modal = this.querySelector('[data-ai-modal]');
      this.dialog = this.querySelector('.dyefor-ai-chat__dialog');
      this.thread = this.querySelector('[data-ai-thread]');
      this.form = this.querySelector('[data-ai-form]');
      this.promptInput = this.querySelector('[data-ai-prompt]');
      this.fileInput = this.querySelector('[data-ai-file]');
      this.fileLabel = this.querySelector('[data-ai-file-label]');
      this.submitButton = this.querySelector('[data-ai-submit]');
      this.message = this.querySelector('[data-ai-message]');
      this.resetButton = this.querySelector('[data-ai-reset]');
      this.closeButtons = this.querySelectorAll('[data-ai-close]');
      this.referenceImage = '';
      this.referenceFileName = '';
      this.currentResult = null;
      this.pollVersion = 0;
      this.promptStarted = false;
      this.busy = false;
      this.composerEnabled = false;
      this.choiceButtons = [];
      this.conversationHistory = [];
      this.progressBubble = null;
      this.progressRow = null;
      this.progressCard = null;
      this.progressTimer = 0;
      this.progressStartedAt = 0;
      this.progressExpectedDurationMs = 0;
      this.progressStage = 'queued';
      this.progressStageStartedAt = 0;
      this.progressValue = 8;
      this.placeholderPreloads = [];
      this.wakeLock = null;
      this.wakeLockRequest = null;
      this.modalOpen = false;
      this.selectedProductType = this.getSelectedProduct();

      if (!this.apiBase || !this.form || !this.openButton || !this.modal) return;

      [
        this.dataset.phonePlaceholder,
        this.dataset.bannerPlaceholder,
        this.dataset.phoneCanvasClear,
        this.dataset.phoneCanvasBlack,
        this.dataset.phoneCanvasWhite,
        this.dataset.bannerCanvasClear,
        this.dataset.bannerCanvasBlack,
        this.dataset.bannerCanvasWhite
      ].forEach(function (source) {
        if (!source) return;
        var image = new Image();
        image.decoding = 'async';
        image.src = source;
        this.placeholderPreloads.push(image);
      }, this);

      this.prepareChatModal();
      this.viewportHandler = this.syncChatViewport.bind(this);
      this.modal.addEventListener('cancel', function (event) {
        event.preventDefault();
        this.closeChat();
      }.bind(this));
      this.openButton.addEventListener('click', this.openChat.bind(this));
      var homeSection = this.closest('.dyefor-personal-home');
      this.categoryOpenButton = homeSection && homeSection.querySelector('[data-ai-studio-open]');
      if (this.categoryOpenButton) {
        this.categoryOpenHandler = this.openChat.bind(this);
        this.categoryOpenButton.addEventListener('click', this.categoryOpenHandler);
      }
      Array.prototype.forEach.call(this.productInputs, function (input) {
        input.addEventListener('change', this.handleProductChange.bind(this));
      }, this);
      if (this.phoneDeviceSelect) {
        this.phoneDeviceSelect.addEventListener('change', this.handlePhoneDeviceChange.bind(this));
      }
      this.form.addEventListener('submit', this.handleMessage.bind(this));
      this.fileInput.addEventListener('change', this.handleFile.bind(this));
      this.resetButton.addEventListener('click', this.startNewIdea.bind(this));
      Array.prototype.forEach.call(this.closeButtons, function (button) {
        button.addEventListener('click', this.closeChat.bind(this));
      }, this);
      this.keydownHandler = this.handleKeydown.bind(this);
      document.addEventListener('keydown', this.keydownHandler);
      this.visibilityHandler = this.handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this.visibilityHandler);
      window.addEventListener('dyefor:device-change', this.handleRememberedDevice.bind(this));

      this.fileLabel.textContent = 'Add a photo';
      this.restorePhoneDevice();
      this.updateProductControls();
      this.resetConversation(false, true);
      this.restoreActiveJob();
    }

    disconnectedCallback() {
      if (this.modalOpen) this.closeChat();
      if (this.categoryOpenButton) this.categoryOpenButton.removeEventListener('click', this.categoryOpenHandler);
      if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler);
      if (this.keydownHandler) document.removeEventListener('keydown', this.keydownHandler);
      this.stopGenerationProgress(false);
      this.releaseWakeLock();
    }

    prepareChatModal() {
      // Upgrade cached section markup too, so it gets the same native modal boundary.
      if (this.modal.tagName !== 'DIALOG') {
        var modal = document.createElement('dialog');
        Array.prototype.forEach.call(this.modal.attributes, function (attribute) {
          modal.setAttribute(attribute.name, attribute.value);
        });
        while (this.modal.firstChild) modal.appendChild(this.modal.firstChild);
        this.modal.replaceWith(modal);
        this.modal = modal;
      }
      var label = this.dialog.getAttribute('aria-labelledby');
      if (label) this.modal.setAttribute('aria-labelledby', label);
      this.dialog.removeAttribute('role');
      this.dialog.removeAttribute('aria-modal');
      this.dialog.setAttribute('tabindex', '-1');
      this.dialog.setAttribute('autofocus', '');
    }

    focusChat() {
      if (this.modalOpen && this.dialog) this.dialog.focus({ preventScroll: true });
    }

    syncChatViewport() {
      if (!this.modalOpen) return;
      var viewport = window.visualViewport;
      var keepLatestVisible = this.thread && this.thread.scrollHeight - this.thread.scrollTop - this.thread.clientHeight < 80;
      this.modal.style.setProperty('--dyefor-ai-viewport-height', (viewport ? viewport.height : window.innerHeight) + 'px');
      this.modal.style.setProperty('--dyefor-ai-viewport-width', (viewport ? viewport.width : window.innerWidth) + 'px');
      this.modal.style.setProperty('--dyefor-ai-viewport-top', (viewport ? viewport.offsetTop : 0) + 'px');
      this.modal.style.setProperty('--dyefor-ai-viewport-left', (viewport ? viewport.offsetLeft : 0) + 'px');
      if (keepLatestVisible) this.scrollThread();
    }

    async ensureSession() {
      var stored = readStorage(SESSION_KEY);
      if (stored && stored.token && Number(stored.expiresAt) > Math.floor(Date.now() / 1000) + 60) {
        return stored.token;
      }
      var response = await fetch(this.apiBase + '/v1/session', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ customerId: this.dataset.customerId || '' }),
        cache: 'no-store'
      });
      var data = await response.json().catch(function () { return {}; });
      if (!response.ok || !data.token) throw apiError(data.error, data.code, response.status);
      writeStorage(SESSION_KEY, data);
      return data.token;
    }

    async request(path, options) {
      var token = await this.ensureSession();
      var settings = options || {};
      settings.headers = Object.assign({}, settings.headers || {}, { authorization: 'Bearer ' + token });
      settings.cache = 'no-store';
      var response = await fetch(this.apiBase + path, settings);
      var data = await response.json().catch(function () { return {}; });
      if (!response.ok) throw apiError(data.error, data.code, response.status);
      return data;
    }

    track(eventType, details) {
      this.request('/v1/event', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(Object.assign({ eventType: eventType }, details || {}))
      }).catch(function () {});
    }

    openChat() {
      if (this.modalOpen) return;
      this.modalOpen = true;
      this.lastFocusedElement = document.activeElement;
      this.modal.hidden = false;
      document.body.style.setProperty('--dyefor-ai-page-top', -window.scrollY + 'px');
      document.documentElement.classList.add('dyefor-ai-chat-open');
      document.body.classList.add('dyefor-ai-chat-open');
      this.syncChatViewport();
      if (typeof this.modal.showModal === 'function') this.modal.showModal();
      this.focusChat();
      window.addEventListener('resize', this.viewportHandler);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.viewportHandler);
        window.visualViewport.addEventListener('scroll', this.viewportHandler);
      }
      this.openButton.setAttribute('aria-expanded', 'true');
      if (this.categoryOpenButton) this.categoryOpenButton.setAttribute('aria-expanded', 'true');
      this.requestWakeLock();
      window.requestAnimationFrame(function () {
        if (this.modalOpen) this.modal.classList.add('is-open');
      }.bind(this));
      this.track('studio_opened', this.selectedProductType ? { productType: this.selectedProductType } : {});
      this.scrollThread();
    }

    closeChat() {
      if (!this.modalOpen) return;
      this.dismissComposerKeyboard();
      this.modalOpen = false;
      this.modal.classList.remove('is-open');
      if (this.modal.open && typeof this.modal.close === 'function') this.modal.close();
      this.modal.hidden = true;
      window.removeEventListener('resize', this.viewportHandler);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.viewportHandler);
        window.visualViewport.removeEventListener('scroll', this.viewportHandler);
      }
      document.body.classList.remove('dyefor-ai-chat-open');
      document.documentElement.classList.remove('dyefor-ai-chat-open');
      document.body.style.removeProperty('--dyefor-ai-page-top');
      this.openButton.setAttribute('aria-expanded', 'false');
      if (this.categoryOpenButton) this.categoryOpenButton.setAttribute('aria-expanded', 'false');
      this.releaseWakeLock();
      if (this.lastFocusedElement === this.categoryOpenButton) {
        this.categoryOpenButton.focus({ preventScroll: true });
        return;
      }
      this.openButton.focus({ preventScroll: true });
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      window.requestAnimationFrame(function () {
        if (!this.modalOpen) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }.bind(this));
    }

    async requestWakeLock() {
      if (!this.modalOpen || document.visibilityState !== 'visible' || !navigator.wakeLock || this.wakeLock || this.wakeLockRequest) return;
      var pending;
      try {
        pending = navigator.wakeLock.request('screen');
        this.wakeLockRequest = pending;
        var lock = await pending;
        if (this.wakeLockRequest !== pending || !this.modalOpen || document.visibilityState !== 'visible') {
          await lock.release();
          return;
        }
        this.wakeLock = lock;
        lock.addEventListener('release', function () {
          if (this.wakeLock === lock) this.wakeLock = null;
        }.bind(this), { once: true });
      } catch (error) {
        // Screen wake locks can be refused by power-saving or browser policies.
      } finally {
        if (this.wakeLockRequest === pending) this.wakeLockRequest = null;
      }
    }

    releaseWakeLock() {
      var lock = this.wakeLock;
      this.wakeLock = null;
      this.wakeLockRequest = null;
      if (lock && !lock.released) lock.release().catch(function () {});
    }

    handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      if (this.modalOpen) this.requestWakeLock();
      if (!this.busy && !this.currentResult) this.restoreActiveJob();
    }

    dismissComposerKeyboard() {
      if (this.promptInput && document.activeElement === this.promptInput) this.promptInput.blur();
    }

    handleKeydown(event) {
      if (!this.modalOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeChat();
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = Array.prototype.filter.call(
        this.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled])'),
        function (element) { return element.offsetParent !== null; }
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    resetConversation(announce, preserveActiveJob) {
      if (this.busy) return;
      this.stopGenerationProgress(true);
      this.pollVersion += 1;
      if (!preserveActiveJob) removeStorage(ACTIVE_JOB_KEY);
      this.referenceImage = '';
      this.referenceFileName = '';
      this.currentResult = null;
      this.promptStarted = false;
      this.progressBubble = null;
      this.progressRow = null;
      this.progressCard = null;
      this.choiceButtons = [];
      this.conversationHistory = [];
      this.selectedProductType = '';
      this.updateProductControls();
      this.answers = {
        productType: '',
        device: '',
        audience: '',
        aestheticLabel: '',
        aesthetic: '',
        caseLayout: '',
        compositionMode: '',
        caseTextResolved: false,
        occasion: '',
        recipient: '',
        recipientIntent: '',
        photoDecision: '',
        age: '',
        ageDecision: '',
        includeAge: false,
        recipientRelationship: '',
        recipientAudience: '',
        interests: '',
        exactText: '',
        textMode: 'exact',
        giftTextResolved: false
      };
      this.thread.replaceChildren();
      this.promptInput.value = '';
      this.fileInput.value = '';
      this.fileLabel.textContent = 'Add a photo';
      this.step = 'product';
      this.addAssistant('What would you like to create?', {
        choices: [
          { label: 'Wrapping Paper', action: this.chooseProduct.bind(this, 'wrapping_paper') },
          { label: 'Banner', action: this.chooseProduct.bind(this, 'banner') },
          { label: 'Phone Case', action: this.chooseProduct.bind(this, 'phone_case') }
        ],
        choicesClass: 'dyefor-ai-chat__choices--products',
        wideChoices: true
      });
      this.setComposer(false, 'Choose a product above');
      this.setBusy(false);
      if (announce) this.announce('Started a new design.');
    }

    chooseProduct(productType) {
      if (this.busy || this.step !== 'product' || !PRODUCT_LABELS[productType]) return;
      this.disablePreviousChoices();
      this.selectedProductType = productType;
      this.answers.productType = productType;
      this.updateProductControls();
      this.addUser(PRODUCT_LABELS[productType]);
      if (productType === 'phone_case') {
        this.askForPhoneDevice();
      } else {
        this.askForGiftOccasion();
      }
      this.setBusy(false);
    }

    askForPhoneDevice() {
      this.step = 'case_device';
      this.setComposer(false, 'Choose your phone model above');
      var message = this.addAssistant('Which iPhone is it for?', { wideChoices: true });
      var field = makeElement('div', 'dyefor-ai-hero__device');
      var label = makeElement('label', '', 'Phone model');
      var select = document.createElement('select');
      select.id = this.phoneDeviceSelect.id + '-chat';
      select.setAttribute('data-ai-chat-device', '');
      label.htmlFor = select.id;
      Array.prototype.forEach.call(this.phoneDeviceSelect.options, function (option) {
        select.appendChild(option.cloneNode(true));
      });
      select.value = this.selectedPhoneDevice();
      field.appendChild(label);
      field.appendChild(select);
      message.content.appendChild(field);
      var choices = this.createChoices([{
        label: 'Continue',
        primary: true,
        action: function () {
          if (this.step !== 'case_device' || !select.value || !this.selectPhoneDevice(select.value)) return;
          select.disabled = true;
          this.disablePreviousChoices();
          this.handlePhoneDeviceChange();
          this.addUser(this.answers.device);
          this.startPhoneCaseConversation();
          this.setBusy(false);
        }.bind(this)
      }]);
      var continueButton = choices.querySelector('button');
      continueButton.disabled = !select.value;
      select.addEventListener('change', function () {
        if (this.step === 'case_device') continueButton.disabled = !select.value;
      }.bind(this));
      message.content.appendChild(choices);
      this.scrollThread();
    }

    addAssistant(text, options) {
      var settings = options || {};
      if (!settings.progress) this.rememberConversation('assistant', text);
      var row = makeElement('div', 'dyefor-ai-chat__message dyefor-ai-chat__message--assistant');
      var avatar = makeElement('span', 'dyefor-ai-chat__message-avatar', '\u2726');
      avatar.setAttribute('aria-hidden', 'true');
      var content = makeElement('div', 'dyefor-ai-chat__message-content');
      if (settings.wideChoices) content.classList.add('has-wide-choices');
      var bubble = makeElement('div', 'dyefor-ai-chat__bubble', text);
      content.appendChild(bubble);
      row.appendChild(avatar);
      row.appendChild(content);
      this.thread.appendChild(row);

      if (settings.progress) {
        bubble.classList.add('is-progress');
        var dots = makeElement('span', 'dyefor-ai-chat__typing-dots');
        dots.setAttribute('aria-hidden', 'true');
        dots.appendChild(makeElement('i'));
        dots.appendChild(makeElement('i'));
        dots.appendChild(makeElement('i'));
        bubble.insertBefore(dots, bubble.firstChild);
      }

      if (settings.choices && settings.choices.length) {
        content.appendChild(this.createChoices(settings.choices, settings.choicesClass));
      }
      this.scrollThread();
      return { row: row, bubble: bubble, content: content };
    }

    addUser(text, imageUrl) {
      this.rememberConversation('user', text);
      var row = makeElement('div', 'dyefor-ai-chat__message dyefor-ai-chat__message--user');
      var bubble = makeElement('div', 'dyefor-ai-chat__bubble');
      if (imageUrl) {
        var image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        bubble.appendChild(image);
      }
      if (text) bubble.appendChild(makeElement('span', '', text));
      row.appendChild(bubble);
      this.thread.appendChild(row);
      this.scrollThread();
      return row;
    }

    rememberConversation(role, text) {
      if (!text) return;
      this.conversationHistory = (this.conversationHistory || []).concat({ role: role, content: String(text).slice(0, 400) }).slice(-12);
    }

    briefHistory(currentMessage) {
      var history = (this.conversationHistory || []).slice();
      var last = history[history.length - 1];
      if (last && last.role === 'user' && last.content === currentMessage) history.pop();
      return history;
    }

    understandingUnavailable(value) {
      this.addAssistant('Sorry, our AI service is temporarily unavailable. Your answer is still in the message box. Please try again later.');
      this.setComposer(true, 'Your saved answer...');
      this.promptInput.value = value;
    }

    createChoices(choices, className) {
      var group = makeElement('div', 'dyefor-ai-chat__choices');
      if (className) group.classList.add(className);
      choices.forEach(function (choice) {
        var button = makeElement('button', 'dyefor-ai-chat__choice', choice.label);
        button.type = 'button';
        if (choice.primary) button.classList.add('is-primary');
        if (choice.suggestion) button.classList.add('is-suggestion');
        button.addEventListener('click', function () {
          if (this.busy || button.disabled) return;
          this.dismissComposerKeyboard();
          this.focusChat();
          choice.action();
        }.bind(this));
        this.choiceButtons.push(button);
        group.appendChild(button);
      }, this);
      return group;
    }

    disablePreviousChoices() {
      if (this.choiceButtons.indexOf(document.activeElement) !== -1) this.focusChat();
      this.choiceButtons.forEach(function (button) { button.disabled = true; });
    }

    getSelectedProduct() {
      var selected = Array.prototype.find.call(this.productInputs || [], function (input) {
        return input.checked && PRODUCT_LABELS[input.value];
      });
      return selected ? selected.value : 'wrapping_paper';
    }

    updateProductControls() {
      Array.prototype.forEach.call(this.productInputs || [], function (input) {
        input.checked = input.value === this.selectedProductType;
      }, this);
      if (this.openButtonLabel) {
        this.openButtonLabel.textContent = this.selectedProductType
          ? 'Create my ' + PRODUCT_LABELS[this.selectedProductType].toLowerCase()
          : 'Create your own';
      }
      var phoneSelected = this.selectedProductType === 'phone_case';
      if (this.phoneDeviceField) this.phoneDeviceField.hidden = !phoneSelected;
      if (this.phoneDeviceSelect) this.phoneDeviceSelect.required = phoneSelected;
      if (this.openButton) this.openButton.disabled = false;
    }

    selectedPhoneDevice() {
      return this.phoneDeviceSelect ? String(this.phoneDeviceSelect.value || '').trim() : '';
    }

    restorePhoneDevice() {
      if (!this.phoneDeviceSelect) return;
      var remembered = '';
      try {
        if (localStorage.getItem(DEVICE_TYPE_STORAGE_KEY) === 'phone') {
          remembered = localStorage.getItem(DEVICE_STORAGE_KEY) || '';
        }
      } catch (error) {}
      this.selectPhoneDevice(remembered);
    }

    selectPhoneDevice(value) {
      if (!this.phoneDeviceSelect) return false;
      var match = Array.prototype.find.call(this.phoneDeviceSelect.options, function (option) {
        return option.value === value;
      });
      this.phoneDeviceSelect.value = match ? match.value : '';
      return Boolean(match && match.value);
    }

    handleRememberedDevice(event) {
      var detail = event && event.detail || {};
      if (detail.type !== 'phone' || detail.family !== 'iphone') return;
      if (this.selectPhoneDevice(String(detail.value || ''))) this.updateProductControls();
    }

    handlePhoneDeviceChange() {
      var device = this.selectedPhoneDevice();
      if (device) {
        try {
          localStorage.setItem(DEVICE_STORAGE_KEY, device);
          localStorage.setItem(DEVICE_TYPE_STORAGE_KEY, 'phone');
        } catch (error) {}
        window.dispatchEvent(new CustomEvent('dyefor:device-change', {
          detail: { value: device, type: 'phone', family: 'iphone' }
        }));
      }
      this.updateProductControls();
      if (this.answers) this.answers.device = device;
    }

    handleProductChange(event) {
      var productType = event.target.value;
      if (!PRODUCT_LABELS[productType] || this.busy) {
        this.updateProductControls();
        return;
      }
      this.resetConversation(false);
      this.chooseProduct(productType);
    }

    async handleMessage(event) {
      event.preventDefault();
      if (this.busy || !this.composerEnabled) return;
      var value = this.promptInput.value.trim();
      if (!value) return;
      this.dismissComposerKeyboard();
      this.focusChat();

      if (this.selectedProductType === 'phone_case' && /^case_(audience|aesthetic|details|layout|text)$/.test(this.step)) {
        await this.handlePhoneCaseMessage(value);
        return;
      }

      if (this.selectedProductType !== 'phone_case' && /^(occasion|recipient|photo|age_inclusion|age_value|interests|exact_text)$/.test(this.step)) {
        await this.handleGiftMessage(value);
        return;
      }

      if (this.step === 'case_photo') {
        if (/^(no|nope|without|no photo|use another layout|skip)/i.test(value)) {
          this.addUser(value);
          this.promptInput.value = '';
          this.answers.caseLayout = 'One focal design on a clear case';
          this.answers.compositionMode = 'transparent_decal';
          this.askForCaseText("No problem, we'll keep it illustration-led.");
          return;
        }
        this.addUser(value);
        this.promptInput.value = '';
        this.addAssistant('Add a photo with the button above, or say no and we will keep it illustration-led.');
        return;
      }

      if (this.step === 'occasion') {
        if (value.length < 2 || value.length > 100) {
          this.announce('Describe the occasion in a few words.');
          this.promptInput.focus();
          return;
        }
        this.answers.occasion = value;
        this.addUser(value);
        this.promptInput.value = '';
        this.disablePreviousChoices();
        if (!this.promptStarted) {
          this.promptStarted = true;
          this.track('prompt_started');
        }
        this.step = 'recipient';
        this.addAssistant(this.occasionAcknowledgement(value) + ' Who are we making it for?');
        this.setComposer(true, 'For example, Ben, my husband...');
        this.promptInput.focus();
        return;
      }

      if (this.step === 'recipient') {
        if (value.length < 2) {
          this.announce('Add the recipient or name.');
          this.promptInput.focus();
          return;
        }
        this.answers.recipient = value;
        this.addUser(value);
        this.promptInput.value = '';
        this.disablePreviousChoices();
        this.askForPhoto();
        return;
      }

      if (this.step === 'photo') {
        if (/^(no|nope|without|no photo|keep it without)/i.test(value)) {
          this.addUser(value);
          this.promptInput.value = '';
          this.skipPhoto(false);
          return;
        }
        this.addUser(value);
        this.promptInput.value = '';
        this.addAssistant('Use the Upload Photo button in the conversation, or type no to continue without one.');
        return;
      }

      if (this.step === 'age_inclusion') {
        var directAge = this.extractAge(value);
        var declined = /^(no|nope|without|leave it off|do not|don't)/i.test(value);
        this.addUser(value);
        this.promptInput.value = '';
        if (declined) {
          this.answers.includeAge = false;
          this.answers.age = '';
          this.askForInterests("Perfect, we'll leave the age off.");
          return;
        }
        if (directAge) {
          this.answers.includeAge = true;
          this.answers.age = directAge;
          this.askForInterests('Lovely, we will include ' + directAge + '.');
          return;
        }
        if (/^(yes|yes please|include|please do|yep|yeah)/i.test(value)) {
          var occasionAge = this.extractAge(this.answers.occasion);
          this.answers.includeAge = true;
          if (occasionAge) {
            this.answers.age = occasionAge;
            this.askForInterests('Lovely, we will include ' + occasionAge + '.');
          } else {
            this.step = 'age_value';
            this.addAssistant('Lovely. What age would you like on the design?');
            this.setComposer(true, 'Type the age...');
            this.promptInput.focus();
          }
          return;
        }
        this.addAssistant('Would you like the age on the design? You can say yes, no, or type the age.');
        return;
      }

      if (this.step === 'age_value') {
        var age = this.extractAge(value);
        if (!age) {
          this.announce('Enter the age as a number.');
          this.promptInput.focus();
          return;
        }
        this.answers.includeAge = true;
        this.answers.age = age;
        this.addUser(value);
        this.promptInput.value = '';
        this.askForInterests('Lovely, we will include ' + age + '.');
        return;
      }

      if (this.step === 'interests') {
        if (value.length < 3) {
          this.announce('Add at least one interest or theme.');
          return;
        }
        this.answers.interests = value;
        this.addUser(value);
        this.promptInput.value = '';
        this.disablePreviousChoices();
        await this.askForWordingSuggestions();
        return;
      }

      if (this.step === 'exact_text') {
        if (value.length > 180) {
          this.announce('Exact wording must be 180 characters or fewer.');
          return;
        }
        this.answers.exactText = value;
        this.answers.textMode = 'exact';
        this.addUser(value);
        this.promptInput.value = '';
        this.disablePreviousChoices();
        this.beginGeneration();
        return;
      }
    }

    markPromptStarted() {
      if (this.promptStarted) return;
      this.promptStarted = true;
      this.track('prompt_started');
    }

    async handlePhoneCaseMessage(value) {
      var currentStep = this.step;
      var maximumLength = currentStep === 'case_text' ? 180 : 300;
      if (value.length < 2 || value.length > maximumLength) {
        this.announce(currentStep === 'case_text'
          ? 'Exact wording must be 180 characters or fewer.'
          : 'Describe the idea in 2 to 300 characters.');
        return;
      }

      this.addUser(value);
      this.promptInput.value = '';
      this.disablePreviousChoices();
      this.dismissComposerKeyboard();
      this.markPromptStarted();
      this.setBusy(true);
      this.setComposer(false, 'Understanding your idea...');
      var thinking = this.addAssistant('Understanding your idea...', { progress: true });
      var understanding = await this.understandCaseBrief(value, currentStep);
      thinking.row.remove();
      this.setBusy(false);
      if (!understanding) { this.understandingUnavailable(value); return; }
      this.applyCaseUnderstanding(value, understanding, currentStep);
      if (this.respondToUnderstanding(understanding)) this.advanceCaseConversation();
    }

    async handleGiftMessage(value) {
      var currentStep = this.step;
      var directAge = /^(age_inclusion|age_value)$/.test(currentStep) && /^\d{1,3}(?:st|nd|rd|th)?$/i.test(value.trim()) ? this.extractAge(value) : '';
      if (directAge) {
        this.addUser(value);
        this.promptInput.value = '';
        this.disablePreviousChoices();
        this.dismissComposerKeyboard();
        this.markPromptStarted();
        this.answers.age = directAge;
        this.answers.includeAge = true;
        this.answers.ageDecision = 'include';
        this.advanceGiftConversation();
        return;
      }
      var maximumLength = currentStep === 'exact_text' ? 180 : 300;
      if (value.length < 2 || value.length > maximumLength) {
        this.announce(/^(age_inclusion|age_value)$/.test(currentStep)
          ? 'Enter the age as a number or word.'
          : (currentStep === 'exact_text'
            ? 'Exact wording must be 180 characters or fewer.'
            : 'Describe the idea in 2 to 300 characters.'));
        return;
      }

      this.addUser(value);
      this.promptInput.value = '';
      this.disablePreviousChoices();
      this.dismissComposerKeyboard();
      this.markPromptStarted();
      this.setBusy(true);
      this.setComposer(false, 'Understanding your idea...');
      var thinking = this.addAssistant('Understanding your idea...', { progress: true });
      var understanding = await this.understandGiftBrief(value, currentStep);
      thinking.row.remove();
      this.setBusy(false);
      if (!understanding) { this.understandingUnavailable(value); return; }
      this.applyGiftUnderstanding(value, understanding, currentStep);
      if (this.respondToUnderstanding(understanding)) this.advanceGiftConversation();
    }

    clearUnderstandingFields(brief) {
      if (brief.contractVersion !== 2 || !brief.conversation) return;
      var fields = brief.conversation.clearFields || [];
      fields.forEach(function (field) {
        if (['occasion', 'recipient', 'recipientRelationship', 'recipientAudience', 'recipientIntent', 'ageDecision', 'photoDecision', 'audience'].indexOf(field) !== -1) this.answers[field] = '';
        if (field === 'designDetails') this.answers.interests = '';
        if (field === 'aesthetic') { this.answers.aesthetic = ''; this.answers.aestheticLabel = ''; }
        if (field === 'compositionMode') { this.answers.compositionMode = ''; this.answers.caseLayout = ''; }
        if (field === 'age') { this.answers.age = ''; this.answers.ageDecision = ''; this.answers.includeAge = false; }
        if (field === 'exactText') {
          this.answers.exactText = '';
          this.answers.caseTextResolved = false;
          this.answers.giftTextResolved = false;
        }
      }, this);
    }

    respondToUnderstanding(brief) {
      if (brief.contractVersion !== 2 || !brief.conversation) return true;
      if (brief.conversation.reply) this.addAssistant(brief.conversation.reply);
      if (brief.conversation.continueFlow === false) {
        this.setComposer(true, 'Tell me a little more...');
        return false;
      }
      return true;
    }

    async understandGiftBrief(value, currentStep) {
      try {
        var response = await this.request('/v1/design-brief', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            text: value,
            productType: this.answers.productType,
            currentStep: currentStep,
            history: this.briefHistory(value),
            known: {
              occasion: this.answers.occasion,
              recipient: this.answers.recipient,
              recipientRelationship: this.answers.recipientRelationship,
              recipientAudience: this.answers.recipientAudience,
              recipientIntent: this.answers.recipientIntent,
              photoDecision: this.answers.photoDecision,
              age: this.answers.age,
              ageDecision: this.answers.ageDecision,
              designDetails: this.answers.interests,
              textResolved: this.answers.giftTextResolved,
              exactText: this.answers.exactText
            }
          })
        });
        if (response && response.brief) return response.brief;
      } catch (error) {}
      return null;
    }

    fallbackGiftUnderstanding(value, currentStep) {
      var text = String(value || '').trim();
      var lower = text.toLowerCase();
      var age = currentStep === 'age_value' || /\b(?:turning|aged?|years? old|\d{1,3}(?:st|nd|rd|th)? (?:birthday|anniversary))\b/i.test(text) ? this.extractAge(text) : '';
      var noPhoto = /\b(no photo|without (?:a )?photo|do not use (?:a )?photo|don't use (?:a )?photo|skip (?:the )?photo)\b/.test(lower) || (currentStep === 'photo' && /^(no|nope|skip|without)$/i.test(text));
      var includePhoto = /\b(use|include|add|with) (?:my |a |the )?(?:photo|picture|portrait)\b/.test(lower);
      var noText = /\b(no text|without text|no wording|without wording|nothing written|leave (?:it )?blank)\b/.test(lower);
      var occasionMatch = text.match(/\b(?:\d{1,3}(?:st|nd|rd|th)? )?(?:birthday|anniversary)|\b(?:wedding|christmas|eid|graduation|new baby|baby shower|engagement|hen party|retirement)\b/i);
      return {
        occasion: occasionMatch ? occasionMatch[0] : '',
        recipient: currentStep === 'recipient' ? text : '',
        recipientIntent: isGenericGiftRecipient(text) ? 'generic' : (currentStep === 'recipient' ? 'specific' : 'unspecified'),
        photoIntent: noPhoto ? 'none' : (includePhoto ? 'include' : 'unspecified'),
        age: age,
        ageIntent: age ? 'include' : (currentStep === 'age_inclusion' && /^(no|nope|without|leave it off)$/i.test(text) ? 'none' : 'unspecified'),
        designDetails: currentStep === 'interests' ? text : '',
        hasDesignDetails: currentStep === 'interests',
        textIntent: noText ? 'none' : (currentStep === 'exact_text' ? 'exact' : 'unspecified'),
        exactText: currentStep === 'exact_text' && !noText ? text : ''
      };
    }

    applyGiftUnderstanding(value, understanding, currentStep) {
      var brief = understanding || this.fallbackGiftUnderstanding(value, currentStep);
      this.clearUnderstandingFields(brief);
      if (brief.occasion) this.answers.occasion = brief.occasion;
      if (brief.contractVersion === 2) {
        if (brief.recipientAudience) this.answers.recipientAudience = brief.recipientAudience;
        if (brief.recipientIntent === 'generic') {
          this.answers.recipient = '';
          this.answers.recipientRelationship = '';
          this.answers.recipientIntent = 'generic';
        } else {
          if (brief.recipient) this.answers.recipient = brief.recipient;
          if (brief.recipientRelationship) this.answers.recipientRelationship = brief.recipientRelationship;
          if (brief.recipient || brief.recipientRelationship || brief.recipientIntent === 'specific') this.answers.recipientIntent = 'specific';
        }
      } else {
        var audienceDescription = genericGiftAudienceFromText(value);
        var relationshipFromBrief = canonicalGiftRelationship(brief.recipient);
        var relationshipFromMessage = giftRelationshipFromText(value);
        var hasNamedRecipient = Boolean(brief.recipient && !relationshipFromBrief && !ambiguousGiftAudienceValue(brief.recipient));
        var genericRecipient = (brief.recipientIntent === 'generic' && !(audienceDescription && hasNamedRecipient))
          || isGenericGiftRecipient(value)
          || Boolean(audienceDescription && !hasNamedRecipient);
        var relationship = genericRecipient ? '' : relationshipFromBrief || relationshipFromMessage;
        var relationshipOnly = Boolean(relationshipFromBrief || (relationshipFromMessage && ambiguousGiftAudienceValue(brief.recipient)));
        if (genericRecipient) {
          this.answers.recipient = '';
          this.answers.recipientRelationship = '';
          this.answers.recipientAudience = audienceDescription;
          this.answers.recipientIntent = 'generic';
        } else {
          if (audienceDescription) this.answers.recipientAudience = audienceDescription;
          if (relationship) {
            this.answers.recipientRelationship = relationship;
            this.answers.recipientAudience = '';
            this.answers.recipientIntent = 'specific';
          }
          if (brief.recipient && !relationshipOnly) {
            this.answers.recipient = brief.recipient;
            this.answers.recipientIntent = 'specific';
          } else if (relationshipOnly && (currentStep === 'occasion' || currentStep === 'recipient' || !this.answers.recipient)) {
            this.answers.recipient = '';
          }
        }
      }

      if (brief.photoIntent === 'none') {
        this.answers.photoDecision = 'none';
        this.referenceImage = '';
        this.referenceFileName = '';
      } else if (brief.photoIntent === 'include') {
        this.answers.photoDecision = 'include';
      }

      if (brief.ageIntent === 'none') {
        this.answers.age = '';
        this.answers.includeAge = false;
        this.answers.ageDecision = 'none';
        this.answers.occasion = String(this.answers.occasion || '').replace(/\b\d{1,3}(?:st|nd|rd|th)?\s+(?=birthday|anniversary)/i, '');
      } else if (brief.age) {
        this.answers.age = String(brief.age);
        this.answers.includeAge = true;
        this.answers.ageDecision = 'include';
      } else if (brief.ageIntent === 'include') {
        this.answers.includeAge = true;
        this.answers.ageDecision = 'include';
      } else if (currentStep === 'age_value' && brief.contractVersion !== 2) {
        var directAge = this.extractAge(value);
        if (directAge) {
          this.answers.age = directAge;
          this.answers.includeAge = true;
          this.answers.ageDecision = 'include';
        }
      }

      if (brief.hasDesignDetails && brief.designDetails && this.hasMeaningfulGiftDesignDetails(brief.designDetails)) {
        this.answers.interests = String(brief.designDetails).slice(0, 300);
      }

      if (brief.textIntent === 'none') {
        this.answers.exactText = '';
        this.answers.textMode = 'exact';
        this.answers.giftTextResolved = true;
      } else if (brief.textIntent === 'exact' && brief.exactText) {
        this.answers.exactText = String(brief.exactText).slice(0, 180);
        this.answers.textMode = 'exact';
        this.answers.giftTextResolved = true;
      } else if (currentStep === 'exact_text' && brief.contractVersion !== 2) {
        this.answers.exactText = value;
        this.answers.textMode = 'exact';
        this.answers.giftTextResolved = true;
      }
    }

    advanceGiftConversation() {
      var noText = this.answers.giftTextResolved && !this.answers.exactText;
      if (!this.answers.occasion) {
        this.askForGiftOccasion();
        return;
      }
      if (!noText && !this.answers.recipient && this.answers.recipientIntent !== 'generic') {
        this.askForGiftRecipient();
        return;
      }
      if (!this.answers.photoDecision) {
        if (this.referenceImage) this.answers.photoDecision = 'include';
        else {
          this.askForPhoto();
          return;
        }
      }
      if (this.answers.photoDecision === 'include' && !this.referenceImage) {
        this.askForPhoto();
        return;
      }
      if (!this.occasionUsesAge()) {
        this.answers.includeAge = false;
        this.answers.age = '';
        this.answers.ageDecision = 'none';
      } else if (!this.answers.ageDecision) {
        if (this.answers.giftTextResolved) {
          this.answers.ageDecision = this.answers.age ? 'include' : 'none';
          this.answers.includeAge = Boolean(this.answers.age);
        } else {
          this.askForAgeInclusion();
          return;
        }
      }
      if (!noText && this.answers.ageDecision === 'include' && !this.answers.age) {
        this.askForGiftAge();
        return;
      }
      if (!this.answers.interests) {
        this.askForInterests();
        return;
      }
      if (this.answers.giftTextResolved) {
        this.beginGeneration();
        return;
      }
      this.askForWordingSuggestions();
    }

    hasMeaningfulGiftDesignDetails(value) {
      var text = String(value || '').toLowerCase().replace(/[\u2018\u2019']/g, ' ');
      if (/\b(?:surprise me|no particular theme|no specific theme|you choose)\b/.test(text)) return true;
      [this.answers.recipient, this.answers.recipientRelationship, this.answers.recipientAudience, this.answers.occasion].forEach(function (phrase) {
        var comparable = String(phrase || '').trim().toLowerCase();
        if (!comparable) return;
        var escaped = comparable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
        text = text.replace(new RegExp('\\b' + escaped + '\\b', 'gi'), ' ');
      });
      text = text
        .replace(/\b\d{1,3}(?:st|nd|rd|th)?\b/g, ' ')
        .replace(/\b(?:first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|birthday|anniversary|wedding|christmas|eid|graduation|engagement|retirement|celebration|baby|shower|hen|party)\b/g, ' ')
        .replace(/\b(?:wrapping|wrap|paper|banner|phone|case|gift|personalised|personalized|custom|matching)\b/g, ' ')
        .replace(/\b(?:pattern|repeat|repeating|design|artwork|image|illustration|graphic|graphics|theme|style|background|idea|ideas)\b/g, ' ')
        .replace(/\b(?:boy|boys|girl|girls|man|men|woman|women|son|sons|daughter|daughters|husband|wife)\b/g, ' ')
        .replace(/\b(?:a|an|the|my|our|for|to|of|on|with|and|please)\b/g, ' ')
        .replace(/[^a-z0-9]+/g, '');
      return Boolean(text);
    }

    async understandCaseBrief(value, currentStep) {
      try {
        var response = await this.request('/v1/design-brief', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            text: value,
            productType: 'phone_case',
            currentStep: currentStep,
            history: this.briefHistory(value),
            known: {
              audience: this.answers.audience,
              aesthetic: this.answers.aestheticLabel,
              designDetails: this.answers.interests,
              compositionMode: this.answers.compositionMode,
              textResolved: this.answers.caseTextResolved,
              exactText: this.answers.exactText
            }
          })
        });
        if (response && response.brief) return response.brief;
      } catch (error) {}
      return null;
    }

    fallbackCaseUnderstanding(value, currentStep) {
      var brief = this.inferCaseBrief(value);
      var hasDetails = currentStep !== 'case_text' && currentStep !== 'case_layout' && brief.hasDesignDetails;
      return {
        audience: brief.audience,
        aesthetic: brief.aesthetic ? brief.aesthetic.label : '',
        designDetails: hasDetails ? [this.answers.interests, brief.original].filter(Boolean).join('. ').slice(0, 300) : '',
        hasDesignDetails: hasDetails,
        compositionMode: brief.compositionMode,
        textIntent: brief.noText ? 'none' : (currentStep === 'case_text' ? 'exact' : 'unspecified'),
        exactText: currentStep === 'case_text' && !brief.noText ? brief.original : ''
      };
    }

    inferCaseBrief(value) {
      var original = String(value || '').trim();
      var brief = original.toLowerCase().replace(/[–—]/g, '-').replace(/\s+/g, ' ');
      var result = {
        original: original,
        audience: '',
        aesthetic: null,
        compositionMode: '',
        caseLayout: '',
        noText: /\b(no text|without text|no wording|without wording|leave (?:it )?blank|nothing written|nothing on it|words? off)\b/.test(brief),
        hasDesignDetails: false
      };

      if (/\b(for him|him|his|man|men|male|boy|boys|husband|boyfriend|dad|father|brother|son)\b/.test(brief)) result.audience = 'For him';
      if (/\b(for her|her|hers|woman|women|female|girl|girls|wife|girlfriend|mum|mom|mother|sister|daughter)\b/.test(brief)) result.audience = 'For her';

      result.aesthetic = PHONE_CASE_AESTHETICS.find(function (aesthetic) {
        return (' ' + brief + ' ').indexOf(' ' + aesthetic.label.toLowerCase() + ' ') !== -1;
      }) || null;

      if (/\b(transparent|see[ -]through|unprinted|clear gaps)\b/.test(brief) && /\b(stripes?|lines|bands|checks?|plaid|gingham)\b/.test(brief)) {
        result.compositionMode = 'transparent_pattern';
        result.caseLayout = this.caseLayoutLabel(result.compositionMode);
      } else if (/\b(photo|photos|portrait|portraits|collage|film strip|photo grid|picture grid)\b/.test(brief)) {
        result.compositionMode = 'photo_layout';
        result.caseLayout = 'A considered photo layout';
      } else if (/\b(full[ -]?bleed|edge[ -]?to[ -]?edge|opaque background|solid background|filled background|background colou?r|background scene)\b/.test(brief)) {
        result.compositionMode = 'full_bleed';
        result.caseLayout = 'Edge-to-edge full-bleed artwork';
      } else if (/\b(border|frame|corners?|around the edge|edge details?|climbing|vine)\b/.test(brief)) {
        result.compositionMode = 'transparent_frame';
        result.caseLayout = 'A light border or corner composition on a clear case';
      } else if (/\b(one focal|single focal|one large|single large|central design|centred design|centered design|decal|monogram)\b/.test(brief)) {
        result.compositionMode = 'transparent_decal';
        result.caseLayout = 'One focal design on a clear case';
      } else if (/\b(transparent|clear|true alpha)\b/.test(brief)) {
        result.compositionMode = 'transparent_scatter';
        result.caseLayout = 'Scattered motifs on a clear case';
      } else if (result.aesthetic && result.aesthetic.defaultCompositionMode) {
        result.compositionMode = result.aesthetic.defaultCompositionMode;
        result.caseLayout = this.caseLayoutLabel(result.compositionMode);
      }

      var exactAesthetic = result.aesthetic && brief === result.aesthetic.label.toLowerCase();
      result.hasDesignDetails = Boolean(!exactAesthetic && (result.compositionMode || original.split(/\s+/).length >= 3));
      return result;
    }

    applyCaseUnderstanding(value, understanding, currentStep) {
      var brief = understanding || this.fallbackCaseUnderstanding(value, currentStep);
      this.clearUnderstandingFields(brief);
      if (brief.audience) this.answers.audience = brief.audience;

      if (brief.aesthetic) {
        if (this.answers.aestheticLabel && this.answers.aestheticLabel.toLowerCase() !== String(brief.aesthetic).toLowerCase() && !brief.compositionMode) {
          this.answers.compositionMode = '';
          this.answers.caseLayout = '';
        }
        var matchedAesthetic = PHONE_CASE_AESTHETICS.find(function (aesthetic) {
          return aesthetic.label.toLowerCase() === String(brief.aesthetic).toLowerCase();
        });
        this.answers.aestheticLabel = matchedAesthetic ? matchedAesthetic.label : String(brief.aesthetic).trim();
        this.answers.aesthetic = matchedAesthetic ? matchedAesthetic.direction : String(brief.aesthetic).trim();
      } else if (currentStep === 'case_aesthetic' && !this.answers.aesthetic && brief.contractVersion !== 2) {
        this.answers.aestheticLabel = value;
        this.answers.aesthetic = value;
      }

      if (brief.hasDesignDetails && brief.designDetails) {
        this.answers.interests = String(brief.designDetails).slice(0, 300);
      }

      if (brief.compositionMode) {
        this.answers.compositionMode = brief.compositionMode;
        this.answers.caseLayout = this.caseLayoutLabel(brief.compositionMode);
      } else if (currentStep === 'case_layout' && brief.contractVersion !== 2) {
        this.answers.compositionMode = this.inferCaseComposition(value);
        this.answers.caseLayout = this.caseLayoutLabel(this.answers.compositionMode);
      }

      if (brief.textIntent === 'none') {
        this.answers.exactText = '';
        this.answers.textMode = 'exact';
        this.answers.caseTextResolved = true;
      } else if (brief.textIntent === 'exact' && brief.exactText) {
        this.answers.exactText = String(brief.exactText).slice(0, 180);
        this.answers.textMode = 'exact';
        this.answers.caseTextResolved = true;
      } else if (currentStep === 'case_text' && brief.contractVersion !== 2) {
        this.answers.exactText = value;
        this.answers.textMode = 'exact';
        this.answers.caseTextResolved = true;
      }
      return brief;
    }

    appendCaseDetails(value) {
      var detail = String(value || '').trim();
      if (!detail) return;
      var existing = String(this.answers.interests || '').trim();
      if (!existing) {
        this.answers.interests = detail;
        return;
      }
      if (existing.toLowerCase().indexOf(detail.toLowerCase()) === -1) {
        this.answers.interests = existing + '. ' + detail;
      }
    }

    caseLayoutLabel(compositionMode) {
      var labels = {
        transparent_pattern: 'Connected pattern with transparent gaps',
        transparent_scatter: 'Scattered motifs on a clear case',
        transparent_decal: 'One focal design on a clear case',
        transparent_frame: 'A light border or corner composition on a clear case',
        full_bleed: 'Edge-to-edge full-bleed artwork',
        photo_layout: 'A considered photo layout'
      };
      return labels[compositionMode] || '';
    }

    applyCaseBrief(value, captureDetails) {
      var brief = this.inferCaseBrief(value);
      if (brief.audience && !this.answers.audience) this.answers.audience = brief.audience;
      if (brief.aesthetic && !this.answers.aesthetic) {
        this.answers.aestheticLabel = brief.aesthetic.label;
        this.answers.aesthetic = brief.aesthetic.direction;
      }
      if (brief.compositionMode) {
        this.answers.compositionMode = brief.compositionMode;
        this.answers.caseLayout = brief.caseLayout;
      }
      if (brief.noText) {
        this.answers.exactText = '';
        this.answers.textMode = 'exact';
        this.answers.caseTextResolved = true;
      }
      if (captureDetails && brief.original) this.answers.interests = brief.original;
      return brief;
    }

    advanceCaseConversation() {
      var aesthetic = PHONE_CASE_AESTHETICS.find(function (item) {
        return item.label.toLowerCase() === String(this.answers.aestheticLabel || '').toLowerCase();
      }, this);
      if (!this.answers.compositionMode && aesthetic && aesthetic.defaultCompositionMode) {
        this.answers.compositionMode = aesthetic.defaultCompositionMode;
        this.answers.caseLayout = this.caseLayoutLabel(aesthetic.defaultCompositionMode);
      }
      if (!this.answers.audience) {
        this.startPhoneCaseConversation();
        return;
      }
      if (!this.answers.aesthetic) {
        this.askForCaseAesthetic();
        return;
      }
      if (!this.answers.interests) {
        this.askForCaseDetails();
        return;
      }
      if (!this.answers.compositionMode) {
        this.askForCaseLayout();
        return;
      }
      if (this.answers.compositionMode === 'photo_layout' && !this.referenceImage) {
        this.askForCasePhoto();
        return;
      }
      if (this.answers.caseTextResolved) {
        this.beginGeneration();
        return;
      }
      this.askForCaseText();
    }

    continueCaseConversation() {
      this.advanceCaseConversation();
    }

    startPhoneCaseConversation() {
      this.step = 'case_audience';
      this.addAssistant("Who's the phone case for?", {
        choices: [
          { label: 'For her', action: this.acceptCaseAudience.bind(this, 'For her') },
          { label: 'For him', action: this.acceptCaseAudience.bind(this, 'For him') }
        ]
      });
      this.setComposer(true, 'For her, for him, or describe who it is for...');
    }

    acceptCaseAudience(value, inferredBrief) {
      if (this.step !== 'case_audience') return;
      var brief = inferredBrief || this.inferCaseBrief(value);
      this.answers.audience = brief.audience || value;
      if (brief.hasDesignDetails) this.applyCaseBrief(value, true);
      this.addUser(value);
      this.promptInput.value = '';
      this.disablePreviousChoices();
      this.markPromptStarted();
      this.advanceCaseConversation();
    }

    askForCaseAesthetic() {
      this.step = 'case_aesthetic';
      var choices = PHONE_CASE_AESTHETICS.map(function (aesthetic) {
        return {
          label: aesthetic.label,
          action: this.acceptCaseAesthetic.bind(this, aesthetic.label, aesthetic.direction)
        };
      }, this);
      this.addAssistant('What kind of look do you fancy? Choose an aesthetic, or describe your own.', {
        choices: choices,
        choicesClass: 'dyefor-ai-chat__choices--aesthetics',
        wideChoices: true
      });
      this.setComposer(true, 'Or type another aesthetic...');
    }

    acceptCaseAesthetic(label, direction, customInput) {
      if (this.step !== 'case_aesthetic') return;
      var brief = this.inferCaseBrief(label);
      var matchedAesthetic = brief.aesthetic;
      this.answers.aestheticLabel = matchedAesthetic ? matchedAesthetic.label : label;
      this.answers.aesthetic = direction || (matchedAesthetic && matchedAesthetic.direction) || label;
      if (customInput && brief.hasDesignDetails) this.applyCaseBrief(label, true);
      this.addUser(label);
      this.promptInput.value = '';
      this.disablePreviousChoices();
      this.advanceCaseConversation();
    }

    askForCaseDetails() {
      this.step = 'case_details';
      this.addAssistant('Lovely choice. What colours, motifs or interests would make it feel personal?');
      this.setComposer(true, 'For example, black and cream, tennis, tiny stars...');
    }

    askForCaseLayout() {
      this.step = 'case_layout';
      this.addAssistant('How would you like the artwork arranged on the case?', {
        choices: [
          { label: 'Scattered on clear', action: this.acceptCaseLayout.bind(this, 'Scattered motifs on a clear case', 'transparent_scatter') },
          { label: 'Pattern with clear gaps', action: this.acceptCaseLayout.bind(this, 'Connected pattern with transparent gaps', 'transparent_pattern') },
          { label: 'One focal design', action: this.acceptCaseLayout.bind(this, 'One focal design on a clear case', 'transparent_decal') },
          { label: 'Border or corners', action: this.acceptCaseLayout.bind(this, 'A light border or corner composition on a clear case', 'transparent_frame') },
          { label: 'Full-bleed artwork', action: this.acceptCaseLayout.bind(this, 'Edge-to-edge full-bleed artwork', 'full_bleed') },
          { label: 'Photo layout', action: this.acceptCaseLayout.bind(this, 'A considered photo layout', 'photo_layout') }
        ]
      });
      this.setComposer(true, 'Describe another layout...');
    }

    inferCaseComposition(value) {
      var layout = String(value || '').toLowerCase();
      if (/transparent|see[ -]through|unprinted|clear gaps/.test(layout) && /pattern|stripe|line|band|check|plaid/.test(layout)) return 'transparent_pattern';
      if (/photo|portrait|collage|film strip|grid/.test(layout)) return 'photo_layout';
      if (/full|edge|all over|all-over|background/.test(layout)) return 'full_bleed';
      if (/border|frame|corner|vine|edge detail/.test(layout)) return 'transparent_frame';
      if (/one|single|focal|central|centre|decal|monogram/.test(layout)) return 'transparent_decal';
      return 'transparent_scatter';
    }

    acceptCaseLayout(label, compositionMode) {
      if (this.step !== 'case_layout') return;
      this.answers.caseLayout = label;
      this.answers.compositionMode = compositionMode;
      this.addUser(label);
      this.promptInput.value = '';
      this.disablePreviousChoices();
      this.advanceCaseConversation();
    }

    askForCasePhoto() {
      this.step = 'case_photo';
      this.addAssistant("Lovely. Add the photo you'd like to feature, or carry on without one.", {
        choices: [
          { label: 'Upload Photo', action: this.requestPhoto.bind(this) },
          { label: 'Continue without a photo', action: this.skipCasePhoto.bind(this) }
        ]
      });
      this.setComposer(true, 'Upload a photo, or type no...');
    }

    skipCasePhoto() {
      if (this.step !== 'case_photo') return;
      this.disablePreviousChoices();
      this.addUser('Continue without a photo');
      this.answers.caseLayout = 'One focal design on a clear case';
      this.answers.compositionMode = 'transparent_decal';
      this.askForCaseText("No problem, we'll keep it illustration-led.");
    }

    askForCaseText(prefix) {
      this.step = 'case_text';
      this.addAssistant((prefix ? prefix + ' ' : '') + 'Would you like any wording? Add a name, initials or a short phrase, or choose no text.', {
        choices: [
          { label: 'No text', action: this.selectNoCaseText.bind(this) }
        ]
      });
      this.setComposer(true, 'Type the exact wording...');
    }

    selectNoCaseText() {
      if (this.step !== 'case_text') return;
      this.disablePreviousChoices();
      this.answers.exactText = '';
      this.answers.textMode = 'exact';
      this.answers.caseTextResolved = true;
      this.addUser('No text');
      this.beginGeneration();
    }

    askForGiftOccasion() {
      this.step = 'occasion';
      this.addAssistant(this.answers.recipient
        ? 'Great start. What are we celebrating for ' + this.answers.recipient + '?'
        : this.answers.recipientRelationship
          ? 'Great start. What are we celebrating for your ' + this.answers.recipientRelationship + '?'
        : "What are we celebrating? Tell me who it's for and any ideas you already have.");
      this.setComposer(true, 'For example, a 30th birthday...');
    }

    askForGiftRecipient() {
      this.step = 'recipient';
      if (this.answers.recipientRelationship) {
        this.addAssistant("What's your " + this.answers.recipientRelationship + "'s name?");
        this.setComposer(true, 'Type their name...');
        return;
      }
      this.addAssistant('What name would you like on the design? Or we can leave it unnamed.');
      this.setComposer(true, 'Their name, or no name...');
    }

    askForGiftAge() {
      this.step = 'age_value';
      this.addAssistant(/anniversary/i.test(this.answers.occasion)
        ? 'Lovely. How many years would you like on the design?'
        : 'Lovely. What age would you like on the design?');
      this.setComposer(true, 'Type the age...');
    }

    extractAge(value) {
      var match = String(value || '').match(/\b(1[0-2]\d|[1-9]?\d)(?:st|nd|rd|th)?\b/i);
      return match ? match[1] : '';
    }

    ordinalAge(value) {
      var number = Number(value);
      if (!number) return String(value || '');
      var remainder = number % 100;
      var suffix = 'th';
      if (remainder < 11 || remainder > 13) {
        if (number % 10 === 1) suffix = 'st';
        if (number % 10 === 2) suffix = 'nd';
        if (number % 10 === 3) suffix = 'rd';
      }
      return number + suffix;
    }

    occasionAcknowledgement(value) {
      var occasion = String(value || '').toLowerCase();
      var age = this.extractAge(value);
      if (occasion.indexOf('birthday') !== -1 && age) {
        return 'A ' + this.ordinalAge(age) + ' birthday, brilliant!';
      }
      if (occasion.indexOf('birthday') !== -1) return 'A birthday, lovely!';
      if (occasion.indexOf('wedding') !== -1) return 'A wedding, how lovely!';
      if (occasion.indexOf('anniversary') !== -1) return 'An anniversary, wonderful!';
      if (occasion.indexOf('baby') !== -1) return 'Lovely news!';
      return 'That sounds worth celebrating!';
    }

    giftPhotoQuestion() {
      if (this.answers.recipientIntent === 'generic') {
        return 'Would you like to include a photo, or keep it illustration-only?';
      }
      return 'Would you like to include a photo?';
    }

    occasionUsesAge() {
      return /birthday|turning|aged|age|anniversary|milestone/i.test(this.answers.occasion);
    }

    askForInterests(prefix) {
      this.step = 'interests';
      var question = this.answers.productType === 'wrapping_paper'
        ? 'What would you like the pattern to include? Add any colours, hobbies, motifs or style ideas.'
        : 'What colours, themes or style would you like for the banner?';
      this.addAssistant((prefix ? prefix + ' ' : '') + question);
      this.setComposer(true, this.answers.productType === 'wrapping_paper'
        ? 'For example, blue dinosaurs and tiny stars...'
        : 'For example, sage green with wildflowers...');
    }

    askForPhoto() {
      this.step = 'photo';
      this.setComposer(true, 'Type no, or upload a photo...');
      if (this.referenceImage) {
        this.answers.photoDecision = 'include';
        this.addAssistant('Photo added.');
        this.advanceGiftConversation();
        return;
      }
      this.addAssistant(this.answers.photoDecision === 'include'
        ? "Lovely. Upload the photo you'd like to use."
        : this.giftPhotoQuestion(), {
        choices: [
          { label: 'Upload Photo', action: this.requestPhoto.bind(this) },
          { label: 'Continue without a photo', action: this.skipPhoto.bind(this) }
        ]
      });
    }

    requestPhoto() {
      if (this.step !== 'photo' && this.step !== 'case_photo') return;
      this.fileInput.click();
    }

    skipPhoto(addMessage) {
      if (this.step !== 'photo') return;
      this.disablePreviousChoices();
      if (addMessage !== false) this.addUser('No');
      this.answers.photoDecision = 'none';
      this.advanceGiftConversation();
    }

    askForAgeInclusion(prefix) {
      if (!this.occasionUsesAge()) {
        this.answers.includeAge = false;
        this.answers.age = '';
        this.answers.ageDecision = 'none';
        this.advanceGiftConversation();
        return;
      }
      this.step = 'age_inclusion';
      var question = /anniversary/i.test(this.answers.occasion)
        ? 'Would you like the number of years on the design?'
        : 'Would you like the age on the design?';
      this.addAssistant((prefix ? prefix + ' ' : '') + question);
      this.setComposer(true, 'Type yes, no, or the age...');
    }

    selectWording(text) {
      if (this.step !== 'wording') return;
      this.disablePreviousChoices();
      this.answers.exactText = text;
      this.answers.textMode = 'exact';
      this.answers.giftTextResolved = true;
      this.addUser(text);
      this.track('wording_selected', { metadata: { source: 'suggested' } });
      this.beginGeneration();
    }

    writeOwnWording() {
      if (this.step !== 'wording') return;
      this.disablePreviousChoices();
      this.addUser('I will write my own');
      this.step = 'exact_text';
      this.addAssistant('Of course. What would you like it to say?', {
        choices: [{ label: 'No text', action: this.selectNoGiftText.bind(this) }]
      });
      this.setComposer(true, 'Type your wording exactly...');
    }

    selectNoGiftText() {
      if (this.step !== 'wording' && this.step !== 'exact_text') return;
      this.disablePreviousChoices();
      this.answers.exactText = '';
      this.answers.textMode = 'exact';
      this.answers.giftTextResolved = true;
      this.addUser('No text');
      this.track('wording_selected', { metadata: { source: 'none' } });
      this.beginGeneration();
    }

    fallbackWordingSuggestions() {
      var age = this.answers.age;
      var ordinal = age ? this.ordinalAge(age) : '';
      var birthday = /birthday/i.test(this.answers.occasion);
      var anniversary = /anniversary/i.test(this.answers.occasion);
      if (this.answers.recipientIntent === 'generic') {
        if (anniversary) {
          return age ? [
            'Happy ' + ordinal + ' Anniversary',
            'Celebrating ' + age + ' Wonderful Years',
            age + ' Years Of Love And Laughter',
            'Cheers To ' + age + ' Happy Years',
            'A Very Happy ' + ordinal + ' Anniversary',
            'Here Is To ' + age + ' Years Together'
          ] : [
            'Happy Anniversary',
            'Celebrating Love And Laughter',
            'Here Is To Many Happy Years',
            'A Very Happy Anniversary',
            'Wishing You Both Every Happiness',
            'Cheers To A Wonderful Anniversary'
          ];
        }
        if (/wedding/i.test(this.answers.occasion)) {
          return [
            'Congratulations On Your Wedding Day',
            'Wishing You A Lifetime Of Happiness',
            'Here Is To Your Forever',
            'Love, Laughter And Happily Ever After',
            'With Love On Your Wedding Day',
            'Celebrating A Beautiful New Beginning'
          ];
        }
        if (birthday) {
          return age ? [
            'Happy ' + ordinal + ' Birthday',
            age + ' Today, Hip Hip Hooray',
            'Cheers To A Brilliant ' + ordinal + ' Birthday',
            'Celebrate Turning ' + age,
            'A Very Happy ' + ordinal + ' Birthday',
            ordinal + ' Birthday Wishes'
          ] : [
            'Happy Birthday',
            'Have A Brilliant Birthday',
            'Hip Hip Hooray, It Is Your Birthday',
            'Birthday Wishes And Happy Times',
            'Celebrate In Style',
            'Have The Happiest Birthday'
          ];
        }
        if (/halloween/i.test(this.answers.occasion)) {
          return [
            'Happy Halloween',
            'Have A Spooktacular Halloween',
            'Trick Or Treat',
            'Frightfully Happy Halloween',
            'A Little Halloween Magic',
            'Wishing You A Hauntingly Happy Halloween'
          ];
        }
        if (/christmas/i.test(this.answers.occasion)) {
          return [
            'Merry Christmas',
            'Have A Wonderful Christmas',
            'Wishing You Christmas Cheer',
            'May Your Christmas Sparkle',
            'Peace And Joy This Christmas',
            'A Very Merry Christmas'
          ];
        }
        if (/eid/i.test(this.answers.occasion)) {
          return [
            'Eid Mubarak',
            'Warm Wishes This Eid',
            'Wishing You A Joyful Eid',
            'Peace And Happiness This Eid',
            'Celebrate A Beautiful Eid',
            'Eid Blessings And Happy Wishes'
          ];
        }
        var genericOccasion = String(this.answers.occasion || '').replace(/\b[a-z]/g, function (letter) {
          return letter.toUpperCase();
        });
        return [
          'Happy ' + genericOccasion,
          genericOccasion + ' Wishes',
          'Celebrate ' + genericOccasion,
          'A Very Happy ' + genericOccasion,
          'Wishing You A Wonderful ' + genericOccasion,
          'Here Is To A Brilliant ' + genericOccasion
        ];
      }
      var interestText = String(this.answers.interests || '');
      var interest = interestText.split(/,|\band\b/i)[0].trim().replace(/\b[a-z]/g, function (letter) {
        return letter.toUpperCase();
      }) || 'Celebration';
      var recipientText = String(this.answers.recipient || '').trim();
      var relationshipOnly = canonicalGiftRelationship(recipientText);
      var recipient = relationshipOnly || /^(my|our|a|the)\b/i.test(recipientText) ? '' : recipientText;
      if (!/^[\p{L}][\p{L}\p{M}\s.'&-]{0,119}$/u.test(recipient)) recipient = '';
      var recipientLabel = recipient || (relationshipOnly ? '' : recipientText.replace(/\b[a-z]/g, function (letter) {
        return letter.toUpperCase();
      })) || 'You';
      var birthdayRecipient = recipient ? ' ' + recipient : ' To ' + recipientLabel;
      if (anniversary) {
        var anniversaryMatch = String(this.answers.occasion || '').match(/\b(\d{1,3})(?:st|nd|rd|th)?\b/i);
        var years = age || (anniversaryMatch && anniversaryMatch[1]) || '';
        var anniversaryOrdinal = years ? this.ordinalAge(years) : '';
        return years ? [
          'Happy ' + anniversaryOrdinal + ' Anniversary ' + recipientLabel,
          recipientLabel + ', Celebrating ' + years + ' Wonderful Years',
          'Congratulations On ' + years + ' Years, ' + recipientLabel,
          recipientLabel + "'s " + anniversaryOrdinal + ' Anniversary Together',
          years + ' Happy Years For ' + recipientLabel,
          recipientLabel + ', Here Is To ' + years + ' Years Of Love'
        ] : [
          'Happy Anniversary ' + recipientLabel,
          recipientLabel + ', Wishing You Many More Happy Years',
          'Celebrating Your Love, ' + recipientLabel,
          recipientLabel + "'s Happy Anniversary Together",
          'Many More Happy Years For ' + recipientLabel,
          recipientLabel + ', Here Is To Love And Laughter'
        ];
      }
      if (/wedding/i.test(this.answers.occasion)) {
        return [
          'Wedding Congratulations, ' + recipientLabel,
          recipientLabel + ', Wishing You A Lifetime Of Happiness',
          'Happy Wedding Day, ' + recipientLabel,
          'With Love On Your Wedding Day, ' + recipientLabel,
          'Here Is To Your Forever, ' + recipientLabel,
          'Celebrating Your Love, ' + recipientLabel
        ];
      }
      if (birthday && age) {
        var birthdayWording = [
          'Happy ' + ordinal + ' Birthday' + birthdayRecipient,
          recipientLabel + ', Wishing You A Brilliant ' + ordinal + ' Birthday',
          'Cheers To Your ' + ordinal + ' Birthday ' + recipientLabel
        ];
        if (/\b(dinosaurs?|dino|t-?rex)\b/i.test(interestText)) {
          return birthdayWording.concat([
            recipientLabel + "'s " + ordinal + ' Birthday Is Dino-Mite',
            'Roaring Into ' + age + ', Happy Birthday ' + recipientLabel,
            'A T-Rexcellent ' + ordinal + ' Birthday, ' + recipientLabel
          ]);
        }
        return birthdayWording.concat([
          recipientLabel + ', Have A Wonderful ' + ordinal + ' Birthday',
          recipientLabel + "'s " + ordinal + ' Birthday Is Worth Celebrating',
          age + ' Today, Happy Birthday ' + recipientLabel
        ]);
      }
      if (birthday) {
        return [
          'Happy Birthday' + birthdayRecipient,
          recipientLabel + ', Wishing You A Brilliant Birthday',
          'Birthday Cheers For ' + recipientLabel,
          recipientLabel + ', Have A Wonderful Birthday',
          recipientLabel + "'s Birthday Is Worth Celebrating",
          'Have The Happiest Birthday, ' + recipientLabel
        ];
      }
      var occasion = String(this.answers.occasion || '').replace(/\b[a-z]/g, function (letter) {
        return letter.toUpperCase();
      });
      return [
        'A Wonderful ' + occasion + ' For ' + recipientLabel,
        recipientLabel + ', Wishing You Every Happiness',
        occasion + ' Wishes For ' + recipientLabel,
        recipientLabel + "'s " + interest + ' ' + occasion,
        interest + ' Celebration Magic For ' + recipientLabel,
        'A Brilliant ' + interest + ' ' + occasion + ' For ' + recipientLabel
      ];
    }

    async askForWordingSuggestions() {
      this.step = 'wording_loading';
      this.setComposer(false, 'Finding the right words...');
      var progress = this.addAssistant('Finding a few words to suit your design...', { progress: true });
      var suggestions = [];
      try {
        var response = await this.request('/v1/wording', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            productType: this.answers.productType,
            occasion: this.answers.occasion,
            recipient: this.answers.recipient,
            recipientIntent: this.answers.recipientIntent,
            age: this.answers.age,
            interests: this.answers.interests
          })
        });
        suggestions = Array.isArray(response.suggestions) ? response.suggestions : [];
      } catch (error) {
        suggestions = [];
      }
      progress.row.remove();
      if (suggestions.length < 6) suggestions = this.fallbackWordingSuggestions();
      suggestions = suggestions.slice(0, 6);
      this.step = 'wording';
      var choices = suggestions.map(function (text) {
        return { label: text, suggestion: true, action: this.selectWording.bind(this, text) };
      }, this);
      choices.push({ label: 'I will write my own', action: this.writeOwnWording.bind(this) });
      choices.push({ label: 'No text', action: this.selectNoGiftText.bind(this) });
      this.addAssistant('Which one feels just right?', { choices: choices });
      this.setComposer(false, 'Choose an option above');
    }

    async handleFile() {
      var file = this.fileInput.files && this.fileInput.files[0];
      if (!file) return;
      if (!/^image\/(png|jpeg|webp)$/.test(file.type) || file.size > MAX_FILE_BYTES) {
        this.fileInput.value = '';
        this.addAssistant('Please choose a PNG, JPEG or WebP image under 8 MB.');
        this.announce('Choose a PNG, JPEG or WebP image under 8 MB.');
        return;
      }
      try {
        this.referenceImage = await fileToDataUrl(file);
        this.referenceFileName = file.name;
        this.fileLabel.textContent = file.name;
        this.addUser('Photo added', this.referenceImage);
        this.track('reference_added', { metadata: { fileType: file.type, fileSize: file.size } });
        if (this.step === 'photo') {
          this.disablePreviousChoices();
          this.answers.photoDecision = 'include';
          this.advanceGiftConversation();
        } else if (this.step === 'case_photo') {
          this.disablePreviousChoices();
          this.askForCaseText('Photo added.');
        } else if (this.step === 'ready') {
          this.disablePreviousChoices();
          this.addAssistant('Photo added. I will include it in the design.');
          this.showGenerationRetry();
        } else {
          this.addAssistant('Photo saved. I will use it when you are ready to create.');
        }
      } catch (error) {
        this.addAssistant(error.message);
        this.announce(error.message);
      } finally {
        this.fileInput.value = '';
      }
    }

    showGenerationRetry() {
      this.step = 'ready';
      this.setComposer(false, 'Try creating again');
      this.addAssistant('Would you like me to try creating your design again?', {
        choices: [
          { label: 'Try again', primary: true, action: this.beginGeneration.bind(this, '') },
          { label: 'Start again', action: this.startNewIdea.bind(this) }
        ]
      });
    }

    buildPrompt() {
      if (this.answers.productType === 'phone_case') {
        var compositionDirections = {
          transparent_pattern: 'Use true alpha for deliberately unprinted gaps in a connected pattern. Keep lines or bands continuous; do not scatter fragments or impose a small-motif count.',
          transparent_scatter: 'Use true transparency with a designed scatter of small, complete motifs. Show roughly 12 to 20 motifs on the finished case; each should usually occupy only 8 to 14 percent of the case height.',
          transparent_decal: 'Use true transparency around one complete focal decal. Keep it comfortably clear of the camera and no larger than about 42 percent of the finished case height.',
          transparent_frame: 'Use true transparency with light edge or corner details. Keep the centre open and every motif modestly scaled.',
          full_bleed: 'Fill every pixel with a genuine opaque colour field, texture, scene or repeat. Never simulate transparency with a grey-and-white grid. Continuous patterns such as stripes must remain connected edge to edge, not become detached motifs. For illustrated motif designs, use several modest motifs rather than one giant cropped object unless explicitly requested.',
          photo_layout: 'Build a deliberate portrait-friendly photo layout with complete faces and safe spacing around the camera area.'
        };
        var caseParts = [
          'Create original, fashion-led decorative print artwork.',
          'Style audience: ' + this.answers.audience + '.',
          'Chosen aesthetic: ' + this.answers.aesthetic + '.',
          'Colours, motifs, interests and visual references: ' + this.answers.interests + '.',
          'Composition: ' + this.answers.caseLayout + '.',
          'Return only flat printable decoration, not a photograph of the finished product.',
          compositionDirections[this.answers.compositionMode] || '',
          'Treat this as a contemporary accessory design, not an occasion card or generic birthday graphic.'
        ];
        if (this.referenceImage) caseParts.push('Use the supplied photo as the identity reference and integrate it naturally into the chosen composition.');
        return caseParts.join(' ');
      }
      var parts = [
        'Create original ' + PRODUCT_LABELS[this.answers.productType].toLowerCase() + ' artwork.',
        'Occasion: ' + this.answers.occasion + '.',
        'Interests and meaningful details: ' + this.answers.interests + '.'
      ];
      if (this.answers.exactText) {
        parts.push('Personalisation is limited to the separately supplied exact wording.');
      } else {
        parts.push('Illustrations only, with no lettering, names, ages, numbers or greetings.');
      }
      if (this.answers.recipientAudience) parts.push('Illustration audience: ' + this.answers.recipientAudience + '.');
      if (this.referenceImage) parts.push('Use the supplied photo as the identity reference and integrate it naturally into the finished composition.');
      return parts.join(' ');
    }

    async beginGeneration() {
      if (this.busy) return;
      var prompt = this.buildPrompt();
      if (prompt.length < 8) {
        this.announce('Tell me a little more about your idea.');
        return;
      }

      this.disablePreviousChoices();
      this.step = 'generating';
      this.setBusy(true);
      this.setComposer(false, 'Starting your design...');
      this.addAssistant("Perfect, let's make that happen for you.");

      var payload = {
        prompt: prompt,
        productType: this.answers.productType,
        device: this.answers.productType === 'phone_case' ? this.answers.device : '',
        exactText: this.answers.exactText,
        textIntent: this.answers.exactText ? 'exact' : 'none',
        mode: 'generate'
      };
      if (this.answers.compositionMode) payload.compositionMode = this.answers.compositionMode;
      if (this.referenceImage) payload.referenceImage = this.referenceImage;

      this.pollVersion += 1;
      var version = this.pollVersion;

      try {
        var job = await this.request('/v1/generate', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload)
        });
        writeStorage(ACTIVE_JOB_KEY, Object.assign({}, job, {
          productType: this.answers.productType,
          conversation: this.answers,
          submittedAt: Date.now(),
          startedAt: 0
        }));
        await this.pollDesign(job, version);
      } catch (error) {
        this.handleGenerationError(error, version);
      }
    }

    handleGenerationError(error, version) {
      if (version !== this.pollVersion) return;
      this.setBusy(false);
      this.stopGenerationProgress(true);
      if (error.code === 'daily_limit' || error.code === 'monthly_budget') {
        removeStorage(ACTIVE_JOB_KEY);
        this.step = 'limited';
        this.setComposer(false, 'Design allowance reached');
        this.addAssistant(error.message);
        this.announce(error.message);
        return;
      }
      var saved = readStorage(ACTIVE_JOB_KEY);
      if (saved && saved.designId && (!error.status || error.status >= 500)) {
        this.step = 'waiting';
        this.setComposer(false, 'Your design is saved');
        this.addAssistant('Your design is saved. Check its progress when your connection is ready.', {
          choices: [
            { label: 'Check my design', primary: true, action: this.restoreActiveJob.bind(this) },
            { label: 'New idea', action: this.startNewIdea.bind(this) }
          ]
        });
        return;
      }
      removeStorage(ACTIVE_JOB_KEY);
      this.step = 'ready';
      this.addAssistant(error.message);
      this.announce(error.message);
      this.showGenerationRetry();
    }

    async pollDesign(job, version) {
      for (var attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
        if (version !== this.pollVersion) return;
        if (attempt) await delay(POLL_INTERVAL_MS);
        if (version !== this.pollVersion) return;
        var status;
        try {
          status = await this.request('/v1/designs/' + encodeURIComponent(job.designId));
        } catch (error) {
          if (version !== this.pollVersion) return;
          if (attempt < 3 || error.status >= 500) continue;
          throw error;
        }
        if (version !== this.pollVersion) return;
        if (status.artworkReady && ['render_queued', 'rendering', 'succeeded'].indexOf(status.status) !== -1) {
          // The flat artwork is customer-ready. Its production mockup continues
          // server-side and the secure product page waits for it when necessary.
          this.rememberGenerationDuration(status);
          var loadedImage = null;
          if (status.productType === 'wrapping_paper' && status.asset && status.asset.url) {
            this.announce('Your artwork is saved. Loading your preview...');
            loadedImage = await loadSavedPreview(status.asset.url, function () {
              return version === this.pollVersion;
            }.bind(this));
            if (version !== this.pollVersion) return;
          }
          this.showResult(status, loadedImage);
          return;
        }
        if (status.status === 'failed') {
          removeStorage(ACTIVE_JOB_KEY);
          throw apiError(status.error, status.code, 400);
        }
        if (status.status === 'generating' && !this.progressBubble) {
          var now = Date.now();
          var generationStartedAt = Date.parse(status.updatedAt);
          if (!Number.isFinite(generationStartedAt) || generationStartedAt > now || now - generationStartedAt > MAX_POLL_ATTEMPTS * POLL_INTERVAL_MS) {
            generationStartedAt = now;
          }
          this.startGenerationProgress(generationStartedAt);
          var activeJob = readStorage(ACTIVE_JOB_KEY);
          if (activeJob && activeJob.designId === job.designId) {
            activeJob.startedAt = this.progressStartedAt;
            writeStorage(ACTIVE_JOB_KEY, activeJob);
          }
        }
        this.updateProgress(Date.now() - this.progressStartedAt, false, status.status);
      }
      throw apiError('This design is taking longer than expected. Return in a moment to see it.', 'poll_timeout', 504);
    }

    restoreActiveJob() {
      if (this.busy) return;
      var job = readStorage(ACTIVE_JOB_KEY);
      if (!job || !job.designId) return;
      if (PRODUCT_LABELS[job.productType]) {
        this.selectedProductType = job.productType;
        this.updateProductControls();
      }
      this.answers = Object.assign(this.answers, job.conversation || {}, { productType: job.productType || this.answers.productType });
      if (this.answers.device) this.selectPhoneDevice(this.answers.device);
      this.updateProductControls();
      this.thread.replaceChildren();
      this.choiceButtons = [];
      this.addAssistant('Welcome back. Checking your saved design...');
      this.step = 'generating';
      this.pollVersion += 1;
      this.setBusy(true);
      this.setComposer(false, 'Checking your design...');
      var version = this.pollVersion;
      return this.pollDesign(job, version).catch(function (error) {
        this.handleGenerationError(error, version);
      }.bind(this));
    }

    showResult(result, loadedImage) {
      this.currentResult = result;
      this.step = 'result';
      this.setBusy(false);
      this.setComposer(false, 'Choose an option above');
      var completedMeter = this.progressCard && this.progressCard.querySelector('.dyefor-ai-chat__generation-meter');
      this.updateProgress(Date.now() - this.progressStartedAt, true);
      this.stopGenerationProgress(true);

      var review = result.textCheck && ['pending', 'review_required', 'unavailable'].indexOf(result.textCheck.status) !== -1;
      this.addAssistant(review ? 'Your design is ready. Please check every word carefully.' : 'Your design is ready.');

      var resultBlock = makeElement('div', 'dyefor-ai-chat__result');
      var showFlatPattern = result.productType === 'wrapping_paper' && result.asset;
      var previewLabel = '';
      if (showFlatPattern) {
        var image = loadedImage || document.createElement('img');
        resultBlock.classList.add('dyefor-ai-chat__result--square-pattern');
        if (!loadedImage) image.src = result.asset.url;
        image.alt = 'Your wrapping paper pattern';
        image.width = result.asset.width || 800;
        image.height = result.asset.height || 800;
        if (loadedImage) {
          var repeat = document.createElement('canvas');
          repeat.width = 900;
          repeat.height = 900;
          repeat.style.width = '100%';
          repeat.style.height = 'auto';
          repeat.style.display = 'block';
          repeat.setAttribute('role', 'img');
          repeat.setAttribute('aria-label', 'Your wrapping paper pattern repeated across the paper');
          var context = repeat.getContext('2d');
          for (var row = 0; row < 3; row += 1) {
            for (var column = 0; column < 3; column += 1) {
              context.drawImage(loadedImage, column * 300, row * 300, 300, 300);
            }
          }
          resultBlock.appendChild(repeat);
        } else {
          resultBlock.appendChild(image);
        }
        previewLabel = 'Your wrapping paper pattern';
      } else if (result.preview && result.preview.url) {
        var renderedImage = document.createElement('img');
        renderedImage.src = result.preview.url;
        renderedImage.alt = result.preview.label || 'Your personalised product preview';
        renderedImage.width = result.preview.width || 800;
        renderedImage.height = result.preview.height || 800;
        resultBlock.appendChild(renderedImage);
        previewLabel = result.preview.label || '';
      } else {
        var canvas = document.createElement('canvas');
        var referenceSources = result.productType === 'phone_case'
          ? [this.dataset.phoneCanvasClear, this.dataset.phoneCanvasBlack, this.dataset.phoneCanvasWhite]
          : [this.dataset.bannerCanvasClear, this.dataset.bannerCanvasBlack, this.dataset.bannerCanvasWhite];
        canvas.className = 'dyefor-ai-chat__instant-preview';
        canvas.width = 800;
        canvas.height = 800;
        canvas.setAttribute('role', 'img');
        canvas.setAttribute('aria-label', result.productType === 'phone_case'
          ? 'Your design on an iPhone case'
          : 'Your design on a 6 x 2 ft banner');
        resultBlock.appendChild(canvas);
        previewLabel = result.productType === 'phone_case'
          ? ((this.answers.device || 'iPhone') + ' case preview')
          : '6 x 2 ft banner preview';
        Promise.all([loadCanvasImage(result.asset.url)].concat(referenceSources.map(loadCanvasImage)))
        .then(function (images) {
          if (result.productType === 'phone_case') drawInstantPhonePreview(canvas, images[0], images.slice(1));
          else drawInstantBannerPreview(canvas, images[0], images.slice(1));
          canvas.setAttribute('data-preview-ready', 'true');
        }).catch(function () {
          var fallback = document.createElement('img');
          fallback.src = result.asset.url;
          fallback.alt = 'Your AI-created personalised artwork';
          fallback.width = result.asset.width || 800;
          fallback.height = result.asset.height || 800;
          fallback.setAttribute('data-preview-fallback', 'true');
          canvas.replaceWith(fallback);
        });
      }
      if (previewLabel) {
        resultBlock.appendChild(makeElement('small', 'dyefor-ai-chat__preview-label', previewLabel));
      }
      resultBlock.appendChild(this.createChoices([
        { label: 'Use this design', primary: true, action: this.useDesign.bind(this) },
        { label: 'Try again', action: this.startNewIdea.bind(this) }
      ], 'dyefor-ai-chat__choices--result'));
      this.thread.appendChild(resultBlock);
      // Keep 100% visible over the result briefly, without delaying its display.
      if (completedMeter) {
        resultBlock.style.position = 'relative';
        completedMeter.style.pointerEvents = 'none';
        completedMeter.title = 'Artwork ready';
        resultBlock.appendChild(completedMeter);
        window.setTimeout(function () { completedMeter.remove(); }, 400);
      }
      this.scrollThread();
      this.announce(review ? 'Design ready. Check the wording carefully.' : 'Your design is ready.');
      this.track('result_viewed', this.eventDetails(result));
    }

    startNewIdea() {
      if (this.busy) return;
      this.resetConversation(true);
    }

    async useDesign() {
      if (this.handoffPending) return;
      if (!this.currentResult || !window.DyeforAIImageBridge) {
        this.addAssistant('The personaliser is still loading. Please try again.');
        return;
      }
      var details = this.eventDetails(this.currentResult);
      this.track('design_selected', details);
      this.track('handoff_started', details);
      this.handoffPending = true;
      this.announce('Opening your design...');
      try {
        var handoff = await this.request('/v1/designs/' + encodeURIComponent(this.currentResult.designId) + '/handoff', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: '{}'
        });
        window.DyeforAIImageBridge.stageHandoff(handoff.handoffUrl, {
          designId: this.currentResult.designId,
          requestId: this.currentResult.requestId,
          assetId: this.currentResult.asset.id
        });
      } catch (error) {
        this.handoffPending = false;
        this.announce('');
        this.track('handoff_failed', Object.assign(details, { errorCode: error.code || 'handoff_failed' }));
        this.addAssistant(error.message || 'The design could not be opened in the personaliser.');
      }
    }

    eventDetails(result) {
      return {
        designId: result.designId,
        requestId: result.requestId,
        productType: result.productType,
        status: result.status,
        metadata: {
          device: this.answers.device || null,
          audience: this.answers.audience || null,
          aesthetic: this.answers.aestheticLabel || this.answers.aesthetic || null,
          compositionMode: this.answers.compositionMode || null
        }
      };
    }

    expectedGenerationDuration() {
      var productType = (this.answers && this.answers.productType) || this.selectedProductType;
      var timings = readStorage(PROGRESS_TIMING_KEY) || {};
      var saved = Number(timings[productType]);
      return Number.isFinite(saved) && saved >= 20000 && saved <= 90000
        ? saved
        : (GENERATION_DURATION_DEFAULTS[productType] || GENERATION_DURATION_DEFAULTS.wrapping_paper);
    }

    rememberGenerationDuration(result) {
      // Later statuses have a different updatedAt and would include mockup time.
      if (!this.progressBubble || !this.progressStartedAt || result.status !== 'render_queued') return;
      var productType = result.productType || (this.answers && this.answers.productType);
      if (!GENERATION_DURATION_DEFAULTS[productType]) return;
      var duration = Date.parse(result.updatedAt) - this.progressStartedAt;
      if (!Number.isFinite(duration) || duration < 10000 || duration > 120000) return;
      var timings = readStorage(PROGRESS_TIMING_KEY) || {};
      if (typeof timings !== 'object' || Array.isArray(timings)) timings = {};
      timings[productType] = Math.round(Math.max(20000, Math.min(90000,
        this.expectedGenerationDuration() * 0.6 + duration * 0.4)));
      writeStorage(PROGRESS_TIMING_KEY, timings);
    }

    estimatedGenerationProgress(elapsed) {
      var duration = this.progressExpectedDurationMs || this.expectedGenerationDuration();
      var ratio = Math.max(0, Number(elapsed) || 0) / duration;
      // The provider has no numeric progress. Ease towards the measured finish
      // time, reserving 100% strictly for the artwork-ready response.
      return ratio <= 1
        ? 14 + 78 * (1 - Math.pow(1 - ratio, 1.7))
        : 92 + Math.min(3, ((ratio - 1) * duration / 15000) * 3);
    }

    startGenerationProgress(startedAt) {
      this.stopGenerationProgress(true);
      this.progressStartedAt = Number(startedAt) || Date.now();
      this.progressExpectedDurationMs = this.expectedGenerationDuration();
      this.progressStage = 'generating';
      this.progressStageStartedAt = this.progressStartedAt;
      this.progressValue = 8;
      var progress = this.addAssistant('Planning the composition...', { progress: true, wideChoices: true });
      progress.row.classList.add('is-generation');
      progress.content.classList.add('is-generation');
      var placeholder = makeElement('div', 'dyefor-ai-chat__generation-placeholder');
      placeholder.classList.add('is-' + this.answers.productType.replace('_', '-'));
      placeholder.setAttribute('role', 'img');
      placeholder.setAttribute('aria-label', 'Creating artwork, 8 percent complete');

      var artwork = makeElement('div', 'dyefor-ai-chat__generation-artwork');
      artwork.setAttribute('aria-hidden', 'true');
      var productType = this.answers.productType;
      var placeholderSource = productType === 'phone_case'
        ? this.dataset.phonePlaceholder
        : productType === 'banner'
          ? this.dataset.bannerPlaceholder
          : '';
      if (placeholderSource) {
        var productRender = makeElement('img', 'dyefor-ai-chat__generation-product');
        productRender.src = placeholderSource;
        productRender.alt = '';
        productRender.loading = 'eager';
        productRender.decoding = 'async';
        artwork.appendChild(productRender);
        placeholder.classList.add('has-product-render');
      } else {
        for (var index = 0; index < 7; index += 1) {
          artwork.appendChild(makeElement('span', 'dyefor-ai-chat__generation-motif motif-' + (index + 1)));
        }
      }
      placeholder.appendChild(artwork);

      var meter = makeElement('div', 'dyefor-ai-chat__generation-meter');
      meter.title = 'Estimated progress';
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 64 64');
      svg.setAttribute('aria-hidden', 'true');
      var track = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      track.setAttribute('class', 'dyefor-ai-chat__generation-ring-track');
      track.setAttribute('cx', '32');
      track.setAttribute('cy', '32');
      track.setAttribute('r', '26');
      var fill = track.cloneNode(false);
      fill.setAttribute('class', 'dyefor-ai-chat__generation-ring-fill');
      svg.appendChild(track);
      svg.appendChild(fill);
      meter.appendChild(svg);
      meter.appendChild(makeElement('strong', 'dyefor-ai-chat__generation-percent', '8%'));
      placeholder.appendChild(meter);
      progress.content.appendChild(placeholder);

      this.progressBubble = progress.bubble;
      this.progressRow = progress.row;
      this.progressCard = placeholder;
      this.progressRing = fill;
      this.progressPercent = meter.querySelector('strong');
      this.lastProgressCopy = '';
      this.updateProgress(Date.now() - this.progressStartedAt);
      this.progressTimer = window.setInterval(function () {
        this.updateProgress(Date.now() - this.progressStartedAt);
      }.bind(this), 1000);
      this.scrollThread();
    }

    stopGenerationProgress(remove) {
      if (this.progressTimer) window.clearInterval(this.progressTimer);
      this.progressTimer = 0;
      if (remove && this.progressRow) this.progressRow.remove();
      this.progressBubble = null;
      this.progressRow = null;
      this.progressCard = null;
      this.progressRing = null;
      this.progressPercent = null;
      this.lastProgressCopy = '';
      this.progressStage = 'queued';
      this.progressStageStartedAt = 0;
      this.progressExpectedDurationMs = 0;
      this.progressValue = 8;
    }

    updateProgress(elapsed, complete, reportedStage) {
      if (!this.progressBubble) return;
      var now = Date.now();
      var stage = complete ? 'succeeded' : (reportedStage || this.progressStage || 'queued');
      if (stage !== this.progressStage) {
        this.progressStage = stage;
        this.progressStageStartedAt = now;
      }
      var stageElapsed = Math.max(0, now - (this.progressStageStartedAt || now));
      var copy = 'Getting your design ready...';
      var estimated = 8 + Math.min(4, stageElapsed / 750);
      if (stage === 'generating') {
        copy = 'Creating your artwork...';
        estimated = this.estimatedGenerationProgress(elapsed);
      } else if (stage === 'render_queued') {
        copy = 'Artwork created. Preparing your product preview...';
        estimated = 84 + Math.min(2, stageElapsed / 1500);
      } else if (stage === 'rendering') {
        copy = 'Building your product preview...';
        estimated = 87 + Math.min(10, (stageElapsed / 8000) * 10);
      } else if (stage === 'succeeded') {
        copy = 'Artwork ready.';
        estimated = 100;
      }
      var textNode = Array.prototype.find.call(this.progressBubble.childNodes, function (node) {
        return node.nodeType === Node.TEXT_NODE;
      });
      if (textNode) textNode.nodeValue = copy;
      else this.progressBubble.appendChild(document.createTextNode(copy));
      var percent = complete ? 100 : Math.min(97, Math.max(this.progressValue || 8, Math.round(estimated)));
      this.progressValue = percent;
      if (this.progressPercent) this.progressPercent.textContent = percent + '%';
      if (this.progressRing) {
        if (complete) this.progressRing.style.transition = 'none';
        this.progressRing.style.strokeDashoffset = String(163.36 * (1 - (percent / 100)));
      }
      if (this.progressCard) this.progressCard.setAttribute('aria-label', complete
        ? 'Artwork ready, 100 percent complete'
        : 'Creating artwork, estimated ' + percent + ' percent complete');
      if (copy !== this.lastProgressCopy) {
        this.lastProgressCopy = copy;
        this.announce(copy);
      }
    }

    setComposer(enabled, placeholder) {
      this.composerEnabled = Boolean(enabled);
      this.promptInput.disabled = !enabled || this.busy;
      this.submitButton.disabled = !enabled || this.busy;
      this.promptInput.placeholder = placeholder || 'Message...';
      if (this.step === 'age_value') {
        this.promptInput.setAttribute('inputmode', 'numeric');
        this.promptInput.setAttribute('pattern', '[0-9]*');
        this.promptInput.setAttribute('aria-label', 'Age');
      } else {
        this.promptInput.setAttribute('inputmode', 'text');
        this.promptInput.removeAttribute('pattern');
        this.promptInput.setAttribute('aria-label', 'Message');
      }
    }

    setBusy(busy) {
      if (busy) {
        this.dismissComposerKeyboard();
        this.focusChat();
      }
      this.busy = Boolean(busy);
      this.dataset.state = busy ? 'generating' : this.step;
      Array.prototype.forEach.call(this.productInputs || [], function (input) {
        input.disabled = this.busy;
      }, this);
      this.fileInput.disabled = this.busy || ['product', 'case_device', 'result'].indexOf(this.step) !== -1;
      this.resetButton.disabled = this.busy;
      this.choiceButtons.forEach(function (button) {
        if (this.busy) button.disabled = true;
      }, this);
      this.promptInput.disabled = this.busy || !this.composerEnabled;
      this.submitButton.disabled = this.busy || !this.composerEnabled;
    }

    announce(text) {
      this.message.textContent = text || '';
    }

    scrollThread() {
      window.requestAnimationFrame(function () {
        this.thread.scrollTop = this.thread.scrollHeight;
      }.bind(this));
    }
  }

  if (!customElements.get('dyefor-ai-hero')) customElements.define('dyefor-ai-hero', DyeforAIHero);
})();
